﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { Column, DataMap, FlexGrid, DataMapEditor, CellRange, CellRangeEventArgs, CellType, _NewRowTemplate } from '@mescius/wijmo.grid';
import { Globalize, asString, asNumber, asArray, asBoolean, asType, asEnum, isString, isDate, DateTime, culture, isNumber, copy, changeType, setAttribute, showPopup, hidePopup, isFunction, createElement, toggleClass, removeClass, closestClass, setAriaLabel, Control, Event, DataType, Key, CollectionView, GlyphClsNames, InputFormElementsClsNames, UtilitesClsNames, setText, escapeRegExp, setChecked, SortDescription, SortNulls, ControlClsNames, getUniqueId, _addCultureInfo, enable, moveFocus, hasClass, addClass, ControlStateClsNames, _registerModule } from '@mescius/wijmo';
import { DropDown, DropDownClsNames } from '@mescius/wijmo.input';
import * as input from '@mescius/wijmo.input';
import * as selfModule from '@mescius/wijmo.grid.filter';
export var ColumnFilterEditorClsNames = {
    filterType: 'wj-filtertype',
    hostElement: 'wj-columnfiltereditor',
    sortButtons: 'wj-sort-buttons'
};
export var ConditionFilterEditorClsNames = {
    hostElement: 'wj-conditionfilter-editor'
};
export var FlexGridFilterClsNames = {
    elemFilter: 'wj-elem-filter',
    filter: 'wj-filter',
    filterOff: 'wj-filter-off',
    filterOn: 'wj-filter-on',
    hostElement: 'wj-columnfiltereditor'
};
export var ValueFilterEditorClsNames = {
    hostElement: 'wj-valuefilter-editor',
    customModalText: 'wj-custom-modal-text',
    modalFooter: 'wj-valuefilter-modal-footer'
};
'use strict';
/**
 * Specifies types of column filter.
 */
export var FilterType;
(function (FilterType) {
    /** No filter. */
    FilterType[FilterType["None"] = 0] = "None";
    /** A filter based on two conditions. */
    FilterType[FilterType["Condition"] = 1] = "Condition";
    /** A filter based on a set of values. */
    FilterType[FilterType["Value"] = 2] = "Value";
    /** A filter that combines condition and value filters. */
    FilterType[FilterType["Both"] = 3] = "Both";
})(FilterType || (FilterType = {}));
/**
 * Implements an Excel-style filter for {@link FlexGrid} controls.
 *
 * To enable filtering on a {@link FlexGrid} control, create an instance
 * of the {@link FlexGridFilter} and pass the grid as a parameter to the
 * constructor. For example:
 *
 * ```typescript
 * import { FlexGrid } from '@mescius/wijmo.grid';
 * import { FlexGridFilter } from '@mescius/wijmo.grid.filter';
 * let flex = new FlexGrid('#theGrid'); // create the grid
 * let filter = new FlexGridFilter(flex); // add a filter to the grid
 * ```
 *
 * Once this is done, a filter icon is added to the grid's column headers.
 * Clicking the icon shows an editor where the user can edit the filter
 * conditions for that column.
 *
 * The {@link FlexGridFilter} class depends on the **wijmo.grid** and
 * **wijmo.input** modules.
 *
 * The example below shows how you can use a {@link FlexGridFilter} to add
 * filtering to a {@link FlexGrid} control:
 *
 * {@sample Grid/FilteringSearching/Excel-likeFilter/Overview/purejs Example}
 */
var FlexGridFilter = /** @class */ (function () {
    /**
     * Initializes a new instance of the {@link FlexGridFilter} class.
     *
     * @param grid The {@link FlexGrid} to filter.
     * @param options Initialization options for the {@link FlexGridFilter}.
     */
    function FlexGridFilter(grid, options) {
        // check dependencies
        //let depErr = 'Missing dependency: FlexGridFilter requires ';
        //assert(wijmo.grid != null, depErr + 'wijmo.grid.');
        //assert(wijmo.input != null, depErr + 'wijmo.input.');
        this._showIcons = true;
        this._showSort = true;
        this._defFilterType = FilterType.Both;
        this._xValueSearch = true;
        this._includeUpdatedRowsInFilter = false;
        this._showIncludeSelection = false;
        // ** events
        /**
         * Occurs after the filter is applied.
         */
        this.filterApplied = new Event();
        /**
         * Occurs when a column filter is about to be edited by the user.
         * Use this event to customize the column filter if you want to
         * override the default settings for the filter.
         *
         * This event fires before the filter editor is created, so the
         * {@link activeEditor} property is null at this point.
         * If you want to customize the editor, use the {@link filterChanging}
         * event.
         *
         * For example, the code below customizes the list of country names
         * in the value filter editor so "Italy" is always the first value:
         *
         * ```typescript
         * new FlexGridFilter(theGrid, {
         *     editingFilter: (s, e) => {
         *         if (e.getColumn().binding == 'country') {
         *
         *             // start with Italy
         *             let vals = ["Italy"];
         *
         *             // append other unique values (except Italy)
         *             let valueFilter = s.getColumnFilter("country", true).valueFilter;
         *             valueFilter.uniqueValues = null;
         *             valueFilter.getUniqueValues().forEach(item => {
         *                 if (item.text != "Italy") {
         *                     vals.push(item.text);
         *                 }
         *             });
         *
         *             // assign custom unique value list to the valueFilter
         *             valueFilter.uniqueValues = vals;
         *             valueFilter.sortValues = false;
         *         }
         *     }
         * });
         * ```
         */
        this.editingFilter = new Event();
        /**
         * Occurs when a column filter is about to be edited by the user.
         *
         * Use this event to customize the filter editor if you want to
         * override its default settings.
         * You can use the {@link activeEditor} property to get a reference
         * to the currently active filter editor.
         *
         * For example, the code below applies a custom sort to the list of
         * country names in the value filter editor so "Italy" is always the
         * first value:
         *
         * ```typescript
         * new FlexGridFilter(theGrid, {
         *     filterChanging: (s, e) => {
         *         if (e.getColumn().binding == "country") {
         *             let edt = s.activeEditor,
         *                 lbHost = edt.hostElement.querySelector('[wj-part=div-values]'),
         *                 lb = Control.getControl(lbHost) as ListBox;
         *             (lb.collectionView as CollectionView).sortComparer = (a: any, b: any) => {
         *                 if (a != b) { // sort Italy first
         *                     if (a == 'Italy') return -1;
         *                     if (b == 'Italy') return +1;
         *                 }
         *                 return null; // use default sort order
         *             }
         *             lb.collectionView.refresh();
         *         }
         *     },
         * });
         * ```
         */
        this.filterChanging = new Event();
        /**
         * Occurs after a column filter has been edited by the user.
         *
         * Use the event parameters to determine the column that owns
         * the filter and whether changes were applied or canceled.
         */
        this.filterChanged = new Event();
        /**
      * Occurs after the exclusiveValueSearch is changed.
      */
        this.exclusiveValueSearchChanged = new Event();
        /**
         * Occurs after the showIncludeSelectionBtn property is changed.
         */
        this._showIncludeSelectionChanged = new Event();
        // initialize filter
        this._filters = [];
        this._g = asType(grid, FlexGrid, false);
        this._g.formatItem.addHandler(this._formatItem.bind(this));
        this._g.itemsSourceChanged.addHandler(this.clear.bind(this));
        var host = this._g.hostElement;
        grid.addEventListener(host, 'mousedown', this._mousedown.bind(this), true);
        grid.addEventListener(host, 'click', this._click.bind(this), true);
        grid.addEventListener(host, 'keydown', this._keydown.bind(this), true);
        // initialize column filters
        this._g.invalidate();
        // apply options
        copy(this, options);
        // init
        this._init();
    }
    /**
     * Basic initialization steps required for FlexGridFilter
     */
    FlexGridFilter.prototype._init = function () {
        var _this = this;
        var grid = this.grid;
        // store updated/added row to exclude from filter, WJM-32107
        var _excludeRowFromFilter = function (index) {
            var g = _this.grid;
            var rowSet = _this.excludedRowsSet;
            if (g != null && g.rows.length > index) {
                if (g.itemsSource) {
                    // check for new row template
                    if (g.rows[index] instanceof _NewRowTemplate) {
                        var data = g.editableCollectionView.currentAddItem || g.rows[index].dataItem;
                        if (data) //WJM-33328
                            rowSet.add(data);
                    }
                    else {
                        // for bound sheets, store underlying dataItem
                        if (g.rows[index].dataItem) //WJM-33273
                            rowSet.add(g.rows[index].dataItem);
                    }
                }
                else {
                    // for unbound sheets, store row instance
                    if (g.rows[index])
                        rowSet.add(g.rows[index]);
                }
            }
        };
        // for flexsheet, save set of newly inserted rows to ignore from filter until next filter change
        if (grid["rowChanged"]) {
            grid["rowChanged"].addHandler(function (s, e) {
                if (e.added) {
                    _excludeRowFromFilter(e.index);
                }
            });
        }
        // also preserve edited rows from filterout
        grid.rowEditEnding.addHandler(function (s, e) {
            _excludeRowFromFilter(e.row);
        });
        // cell edit ending
        grid.cellEditEnded.addHandler(function (s, e) {
            _excludeRowFromFilter(e.row);
        });
    };
    Object.defineProperty(FlexGridFilter.prototype, "excludedRowsSet", {
        /**
         * Gets/set the Set that contains rows updated after filter is applied, WJM-32107
         *
         */
        get: function () {
            var g = this.grid;
            if (g["selectedSheet"]) {
                g = g["selectedSheet"];
            }
            if (!g["_newAddedRowsSet"]) {
                // use weak to not worry about remove individual row item instance
                g["_newAddedRowsSet"] = new WeakSet();
            }
            return g["_newAddedRowsSet"];
        },
        set: function (value) {
            var g = this.grid;
            if (g["selectedSheet"]) {
                g = g["selectedSheet"];
            }
            g["_newAddedRowsSet"] = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "grid", {
        /**
         * Gets a reference to the {@link FlexGrid} that owns this filter.
         */
        get: function () {
            return this._g;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "filterColumns", {
        /**
         * Gets or sets an array containing the names or bindings of the columns
         * that have filters.
         *
         * Setting this property to null or to an empty array adds filters to
         * all columns.
         */
        get: function () {
            return this._filterColumns;
        },
        set: function (value) {
            this._filterColumns = asArray(value);
            this.clear();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "showFilterIcons", {
        /**
         * Gets or sets a value indicating whether the {@link FlexGridFilter} adds filter
         * editing buttons to the grid's column headers.
         *
         * If you set this property to false, then you are responsible for providing
         * a way for users to edit, clear, and apply the filters.
         *
         * The default value for this property is **true**.
         */
        get: function () {
            return this._showIcons;
        },
        set: function (value) {
            if (value != this.showFilterIcons) {
                this._showIcons = asBoolean(value);
                if (this._g) {
                    this._g.invalidate();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "showSortButtons", {
        /**
         * Gets or sets a value indicating whether the filter editor should include
         * sort buttons.
         *
         * By default, the editor shows sort buttons like Excel does. But since users
         * can sort columns by clicking their headers, sort buttons in the filter editor
         * may not be desirable in some circumstances.
         *
         * The default value for this property is **true**.
         */
        get: function () {
            return this._showSort;
        },
        set: function (value) {
            this._showSort = asBoolean(value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "reApplyFilterOnUpdate", {
        /**
     * Gets or sets a value indicating whether to apply filter on the updated
     * rows.
     * If this property is set to true, then events such as row update,
     * row insertion, sorting etc refreshes filter and rows which do not pass
     * filter gets filtered out.
     * If this property is set to false, then filter is not applied immediately
     * and we need to click the apply button again to refresh filter.
     * For complete excel behavior you also need to set the refreshOnEdit property {@link CollectionView.refreshOnEdit}
     * of CollectionView to false
     *
     * ```typescript
     * // For flexgrid
     * flexgrid.collectionView.refreshOnEdit = false;
     *
     * // For flexsheet
     * flexSheet.itemsSourceChanged.addHandler((s, e) => {
     *   if(s.collectionView){
     *     s.collectionView.refreshOnEdit = false;
     *   }
     * });
     * ```
     *
     * The default value for this property is **false**.
     */
        get: function () {
            return this._includeUpdatedRowsInFilter;
        },
        set: function (value) {
            if (value === this._includeUpdatedRowsInFilter) {
                return;
            }
            this._includeUpdatedRowsInFilter = asBoolean(value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "showIncludeSelection", {
        /**
         * Gets or sets a value that indicates whether the
         * value filter should display excel like 'Add current selection to filter' option
         * The default value for this property is **False**
         */
        get: function () {
            return this._showIncludeSelection;
        },
        set: function (value) {
            if (value != this._showIncludeSelection) {
                this._showIncludeSelection = asBoolean(value, false);
                this._onShowIncludeSelectionChanged();
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Gets the filter for the given column.
     *
     * @param col The {@link Column} that the filter applies to (or column name or index).
     * If the specified column does not exist, the method returns null.
     * @param create Whether to create the filter if it does not exist.
     */
    FlexGridFilter.prototype.getColumnFilter = function (col, create) {
        if (create === void 0) { create = true; }
        // get the column by name or index, check type
        col = this._asColumn(col);
        if (col) { // TFS 418511
            // look for the filter
            for (var i = 0; i < this._filters.length; i++) {
                if (this._filters[i].column == col) {
                    return this._filters[i];
                }
            }
            // not found, create one now
            if (create && col.binding) {
                var cf = new ColumnFilter(this, col);
                this._filters.push(cf);
                return cf;
            }
        }
        // not found, not created
        return null;
    };
    Object.defineProperty(FlexGridFilter.prototype, "defaultFilterType", {
        /**
         * Gets or sets the default filter type to use.
         *
         * This value can be overridden in filters for specific columns.
         * For example, the code below creates a filter that filters by
         * conditions on all columns except the "ByValue" column:
         *
         * ```typescript
         * import { FlexGridFilter, FilterType } from '@mescius/wijmo.grid.filter';
         * let filter = new FlexGridFilter(flex);
         * filter.defaultFilterType = FilterType.Condition;
         * let col = flex.getColumn('ByValue'),
         *     cf = filter.getColumnFilter(col);
         * cf.filterType = FilterType.Value;
         * ```
         *
         * The default value for this property is **FilterType.Both**.
         */
        get: function () {
            return this._defFilterType;
        },
        set: function (value) {
            value = asEnum(value, FilterType, false);
            if (value != this.defaultFilterType) {
                this._defFilterType = value;
                this._g.invalidate();
                this.clear();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "exclusiveValueSearch", {
        /**
         * Gets or sets a value that determines whether the filter should
         * include only values selected by the {@link ValueFilter.filterText}
         * property.
         *
         * The default value for this property is **true**, which matches
         * Excel's behavior.
         *
         * Set it to false to disable this behavior, so searching only affects
         * which items are displayed on the list and not which items are
         * included in the filter.
         */
        get: function () {
            return this._xValueSearch;
        },
        set: function (value) {
            value = asBoolean(value);
            if (value !== this._xValueSearch) {
                this._xValueSearch = value;
                this.onExclusiveValueSearchChanged();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "filterDefinition", {
        /**
         * Gets or sets the current filter definition as a JSON string.
         *
         * The {@link filterDefinition} includes information about all
         * currently active column filters. It does not include data maps
         * because data maps are not serializable.
         */
        get: function () {
            var _this = this;
            var def = {
                defaultFilterType: this.defaultFilterType,
                filters: []
            };
            this._filters.forEach(function (cf) {
                var cfc = cf.conditionFilter, cfv = cf.valueFilter, hasUniqueValues = cfv.uniqueValues && cfv.uniqueValues.length;
                if (cf && cf.column && cf.column.binding) {
                    // check whether we have to save this column filter
                    if (cf.isActive || hasUniqueValues || cf.filterType != _this.defaultFilterType) {
                        var cfDef = {
                            binding: cf.column.binding
                        };
                        // save condition/value filters
                        if (cfc.isActive) {
                            var c1 = cfc.condition1, c2 = cfc.condition2;
                            cfDef = {
                                binding: cf.column.binding,
                                type: 'condition',
                                condition1: { operator: c1.operator, value: c1.value },
                                and: cfc.and,
                                condition2: { operator: c2.operator, value: c2.value }
                            };
                        }
                        else if (cfv.isActive || hasUniqueValues) {
                            cfDef = {
                                binding: cf.column.binding,
                                type: 'value',
                                uniqueValues: cfv.uniqueValues,
                                sortValues: cfv.sortValues,
                                maxValues: cfv.maxValues,
                                exclusiveValueSearch: cfv.exclusiveValueSearch,
                                showValues: cfv.showValues
                            };
                        }
                        // save filter type (TFS 345363)
                        if (cf.filterType != _this.defaultFilterType) {
                            cfDef.filterType = cf.filterType;
                        }
                        // add to list
                        def.filters.push(cfDef);
                    }
                }
            });
            return JSON.stringify(def);
        },
        set: function (value) {
            // make sure the value is a string
            value = asString(value);
            // empty/null clears filter
            this.clear();
            // if a value was provided, parse it
            if (value) {
                var def = JSON.parse(value);
                this.defaultFilterType = def.defaultFilterType;
                var _loop_1 = function (i) {
                    // get the column for this filter
                    var cfs = def.filters[i], col = this_1._asColumn(cfs.binding);
                    // if not found, create a fake column (column may have been removed: TFS 469714)
                    if (!col) {
                        col = new Column({ binding: cfs.binding });
                    }
                    // get the column filter for this column
                    var cf = this_1.getColumnFilter(col, true);
                    if (cf) {
                        // copy filterType (before applying filter definition) (TFS 345363)
                        if (cfs.filterType != null) {
                            cf.filterType = asEnum(cfs.filterType, FilterType);
                        }
                        // copy condition/value filters
                        switch (cfs.type) {
                            case 'condition':
                                var cfc = cf.conditionFilter;
                                cfc.condition1.value = col.dataType == DataType.Date // handle times/times: TFS 125144, 143453
                                    ? changeType(cfs.condition1.value, col.dataType, null)
                                    : cfs.condition1.value;
                                cfc.condition1.operator = cfs.condition1.operator;
                                cfc.and = cfs.and;
                                cfc.condition2.value = col.dataType == DataType.Date
                                    ? changeType(cfs.condition2.value, col.dataType, null)
                                    : cfs.condition2.value;
                                cfc.condition2.operator = cfs.condition2.operator;
                                break;
                            case 'value':
                                var cfv_1 = cf.valueFilter;
                                cfv_1.uniqueValues = cfs.uniqueValues;
                                ['sortValues', 'maxValues', 'exclusiveValueSearch'].forEach(function (prop) {
                                    if (cfs[prop] != null) { // TFS 361036
                                        cfv_1[prop] = cfs[prop];
                                    }
                                });
                                cfv_1.showValues = cfs.showValues;
                                break;
                        }
                    }
                };
                var this_1 = this;
                for (var i = 0; i < def.filters.length; i++) {
                    _loop_1(i);
                }
            }
            // done, apply new filter
            this.apply();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FlexGridFilter.prototype, "activeEditor", {
        /**
         * Gets the active {@link ColumnFilterEditor}.
         *
         * This property allows you to customize the filter editor when
         * handling the {@link filterChanging} event.
         * It returns null when no filters are being edited.
         */
        get: function () {
            return Control.getControl(this._divEdt);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Shows the filter editor for the given grid column.
     *
     * @param col The {@link Column} that contains the filter to edit.
     * @param ht A {@link wijmo.grid.HitTestInfo} object containing the range of the cell
     * that triggered the filter display.
     * @param ref An HTMLElement to use as a reference for positioning the editor.
     */
    FlexGridFilter.prototype.editColumnFilter = function (col, ht, ref) {
        var _this = this;
        var g = this._g;
        // close current editor if any
        this.closeEditor();
        // resolve column (by name, index, or reference)
        col = this._asColumn(col);
        // raise onEditingFilter event (before creating the editor)
        var e = new CellRangeEventArgs(g.cells, new CellRange(-1, col.index));
        if (!this.onEditingFilter(e)) {
            return;
        }
        // get the filter and create the editor
        // (so users can get to it from the filterChanging event: TFS 389763)
        var div = createElement("<div class=\"" + DropDownClsNames.dropDownPanel + "\"></div>"), cf = this.getColumnFilter(col), edt = new ColumnFilterEditor(div, cf, this.showSortButtons);
        this._divEdt = div;
        this._edtCol = col;
        if (g.rightToLeft) {
            div.dir = 'rtl';
        }
        // raise filterChanging event (editor is already available: TFS 389763)
        if (!this.onFilterChanging(e)) {
            this._divEdt = this._edtCol = null;
            return;
        }
        e.cancel = true; // assume the changes will be canceled
        // apply filter when it changes
        edt.filterChanged.addHandler(function () {
            e.cancel = false; // the changes were not canceled
            setTimeout(function () {
                if (!e.cancel) {
                    _this.apply();
                }
            }, edt._edtVal && edt._edtVal._isFiltering ? Control._SEARCH_DELAY + 200 : 0); // WJM-25294
        });
        // close editor when editor button is clicked
        edt.buttonClicked.addHandler(function () {
            _this.closeEditor();
            g.focus();
            _this.onFilterChanged(e);
        });
        // close editor when it loses focus (changes are not applied)
        edt.lostFocus.addHandler(function () {
            setTimeout(function () {
                var ctl = Control.getControl(_this._divEdt);
                if (ctl && !ctl.containsFocus()) {
                    _this.closeEditor();
                }
            }, 10); //200); // let others handle it first
        });
        // find the actual column index 
        // (may be different from the binding column index, TFS 336015, 404275, 466783)
        var colIndex = ht ? ht.col : col.index, colByIndex = g.columns[colIndex];
        if (!ht && (!colByIndex || colByIndex.binding != col.binding)) {
            colIndex = g.selection.leftCol;
        }
        // commit any pending row edits and scroll the column into view
        // use actual column here instead of binding column (TFS 334680)
        g._edtHdl._commitRowEdits(); // TFS 306203
        g.scrollIntoView(-1, colIndex, true);
        // get the header cell to position the editor
        var ch = g.columnHeaders, r = ht && ht.panel && ch && ht.panel._uid == ch._uid ? ht.row : ch.rows.length - 1, c = colIndex, hdrCell = ref || ch.getCellElement(r, c), rc = hdrCell ? null : ch.getCellBoundingRect(r, c);
        // show the editor
        if (hdrCell) {
            showPopup(div, hdrCell, false, false, false);
        }
        else {
            showPopup(div, rc);
        }
        // update aria-expanded attribute on both buttons (visible and hidden)
        this._setAriaExpanded(hdrCell, true);
        this._setAriaExpanded(g.cells.getCellElement(-1, c), true);
        // give the focus to the editor's first visible input element
        var inputs = edt.hostElement.querySelectorAll('input');
        for (var i = 0; i < inputs.length; i++) {
            var el = inputs[i];
            if (el.offsetHeight > 0 && el.tabIndex > -1 && !el.disabled) {
                el.focus();
                break;
            }
        }
        if (!edt.containsFocus()) { // just in case...
            edt.focus();
        }
    };
    FlexGridFilter.prototype._setAriaExpanded = function (cell, value) {
        if (cell) {
            var btn = cell.querySelector('.' + FlexGridFilter._WJC_FILTER);
            setAttribute(btn, 'aria-expanded', value);
        }
    };
    /**
     * Closes the filter editor.
     */
    FlexGridFilter.prototype.closeEditor = function () {
        var g = this._g, edt = Control.getControl(this._divEdt), col = this._edtCol;
        if (edt) {
            hidePopup(edt.hostElement, function () {
                edt.dispose(); // dispose of editor to avoid memory leaks
            });
        }
        if (col) {
            var p = g.columnHeaders, cell = p.rows.length ? p.getCellElement(p.rows.length - 1, col.index) : null;
            this._setAriaExpanded(cell, false);
            cell = g.cells.getCellElement(-1, col.index);
            this._setAriaExpanded(cell, false);
        }
        this._divEdt = null;
        this._edtCol = null;
    };
    /**
     * Applies the current column filters to the grid.
     */
    FlexGridFilter.prototype.apply = function () {
        var view = this._g.collectionView;
        if (view) {
            // commit any pending edits (TFS 271476)
            var ecv = this._g.editableCollectionView;
            if (ecv) {
                ecv.commitEdit();
                ecv.commitNew();
            }
            // WJM-35741
            // The edited list needs to be cleared before the filter is set to the collection view
            this.excludedRowsSet = null;
            // apply the filter (TFS 313344)
            view.filter = this._filter.bind(this);
        }
        // apply filter definition if the collectionView supports that
        var updateFilterDefinition = view ? view['updateFilterDefinition'] : null;
        if (isFunction(updateFilterDefinition)) {
            updateFilterDefinition.call(view, this);
        }
        // and fire the event
        this.onFilterApplied();
    };
    /**
     * Clears all column filters.
     */
    FlexGridFilter.prototype.clear = function () {
        if (this._filters.length) {
            this._filters = [];
            this.apply();
        }
    };
    /**
     * Raises the {@link filterApplied} event.
     */
    FlexGridFilter.prototype.onFilterApplied = function (e) {
        this.filterApplied.raise(this, e);
    };
    /**
     * Raises the {@link editingFilter} event.
     *
     * @param e {@link CellRangeEventArgs} that contains the event data.
     * @return True if the event was not canceled.
     */
    FlexGridFilter.prototype.onEditingFilter = function (e) {
        this.editingFilter.raise(this, e);
        return !e.cancel;
    };
    /**
     * Raises the {@link filterChanging} event.
     *
     * @param e {@link CellRangeEventArgs} that contains the event data.
     * @return True if the event was not canceled.
     */
    FlexGridFilter.prototype.onFilterChanging = function (e) {
        this.filterChanging.raise(this, e);
        return !e.cancel;
    };
    /**
     * Raises the {@link filterChanged} event.
     */
    FlexGridFilter.prototype.onFilterChanged = function (e) {
        this.filterChanged.raise(this, e);
    };
    /**
     * Raises the {@link exclusiveValueSearchChanged} event.
     */
    FlexGridFilter.prototype.onExclusiveValueSearchChanged = function (e) {
        this.exclusiveValueSearchChanged.raise(this, e);
    };
    /**
     * Raises the {@link showIncludeSelectionChanged} event.
     */
    FlexGridFilter.prototype._onShowIncludeSelectionChanged = function (e) {
        this._showIncludeSelectionChanged.raise(this, e);
    };
    // ** implementation
    // get a column by name, index, or reference
    FlexGridFilter.prototype._asColumn = function (col) {
        return isString(col) ? this._g.getColumn(col, true) :
            isNumber(col) ? this._g.columns[col] :
                asType(col, Column, false);
    };
    // CollectionView filter function
    FlexGridFilter.prototype._filter = function (item) {
        // exclude preserved(added/updated after filter applied) rows from filter, WJM-32107
        var excludedRowSet = this.excludedRowsSet;
        if (!this.reApplyFilterOnUpdate && excludedRowSet.has(item)) {
            return true;
        }
        var f = this._filters;
        for (var i = 0; i < f.length; i++) {
            var filter = f[i];
            // skip this column (ValueFilter uses this to build 
            // unique value lists: TFS 467407, 467185)
            if (filter.column == FlexGridFilter._skipColumn) {
                continue;
            }
            // apply 
            if (!filter.apply(item)) {
                return false;
            }
        }
        return true;
    };
    // handle the formatItem event to add filter icons to the column header cells
    FlexGridFilter.prototype._formatItem = function (s, e) {
        // format ColumnHeader cells elements
        if (e.panel == s.columnHeaders) {
            // get column, binding column
            var g = this._g, rng = g.getMergedRange(e.panel, e.row, e.col) || new CellRange(e.row, e.col), col = g.columns[rng.col], bCol = g._getBindingColumn(e.panel, e.row, col), cell = e.cell;
            // check that the row is valid for the filter icon
            if (rng.row2 == e.panel.rows.length - 1 || col != bCol) {
                // get the filter for this column
                var cf = this.getColumnFilter(bCol, this.defaultFilterType != FilterType.None);
                // honor filterColumns property
                var fCols = this._filterColumns;
                if (fCols && fCols.length && fCols.indexOf(bCol.binding) < 0 && fCols.indexOf(bCol.name) < 0) {
                    cf = null;
                }
                // update filter classes (used for styling)
                if (cf) {
                    toggleClass(cell, FlexGridFilterClsNames.filterOn, cf.isActive);
                    toggleClass(cell, FlexGridFilterClsNames.filterOff, !cf.isActive);
                }
                else {
                    removeClass(cell, FlexGridFilterClsNames.filterOn);
                    removeClass(cell, FlexGridFilterClsNames.filterOff);
                }
                // if we have a filter, add the filter button
                if (cf && cf.filterType != FilterType.None) {
                    // filter button in the visible column header
                    if (this._showIcons) {
                        this._addFilterButton(bCol, cf, cell);
                    }
                    // filter button in the hidden columnheader cell
                    if (e.row == 0) {
                        cell = g.cells.getCellElement(-1, e.col);
                        if (cell) {
                            this._addFilterButton(col, cf, cell);
                        }
                    }
                }
            }
        }
    };
    // add a filter icon to a cell
    FlexGridFilter.prototype._addFilterButton = function (col, cf, cell) {
        var cls = FlexGridFilter._WJC_FILTER, btn = createElement("<button class=\"" + InputFormElementsClsNames.btn + " " + GlyphClsNames.btnGlyph + " " + UtilitesClsNames.right + " " + cls + '" type="button" tabindex="-1">' +
            ("<span class=" + GlyphClsNames.filter + "></span>") +
            '</button>');
        setAriaLabel(btn, culture.FlexGridFilter.ariaLabels.edit + ' ' + col.header);
        setAttribute(btn, 'aria-haspopup', 'dialog'); // http://w3c.github.io/aria/#aria-haspopup
        setAttribute(btn, 'aria-expanded', false);
        setAttribute(btn, 'aria-describedby', col.describedById);
        setAttribute(btn, 'aria-pressed', cf.isActive);
        // append to cell (TFS 323622, 323934, 325997)
        if (!cell.querySelector('.' + cls)) {
            if (cell.children.length == 1) { // treat single div as cell (e.g. v-center, MS request)
                cell = cell.querySelector('div') || cell;
            }
            cell.insertBefore(btn, cell.firstChild); // first child, float right
            //cell.appendChild(btn);
        }
    };
    // remember which filter is visible, don't show it again on click
    FlexGridFilter.prototype._mousedown = function (e) {
        this._edtColPrev = this._edtCol;
    };
    // handle clicks to show/hide the filter editor
    FlexGridFilter.prototype._click = function (e) {
        if (this._toggleEditor(e)) {
            e.stopPropagation();
            e.preventDefault();
        }
    };
    // toggle filter editor on mousedown/click
    FlexGridFilter.prototype._toggleEditor = function (e) {
        var _this = this;
        if (!e.defaultPrevented && e.button == 0) { // start actions on left button only: TFS 114623
            if (closestClass(e.target, FlexGridFilter._WJC_FILTER)) {
                var g = this._g, ht_1 = g.hitTest(e.target);
                if (!ht_1.panel) {
                    ht_1 = g.hitTest(e);
                }
                if (ht_1.panel && g.columnHeaders && g.cells && (ht_1.panel._uid == g.columnHeaders._uid || (ht_1.panel._uid == g.cells._uid && ht_1.row == -1))) {
                    var rng = g.getMergedRange(ht_1.panel, ht_1.row, ht_1.col) || new CellRange(ht_1.row, ht_1.col), col = g.columns[rng.col], // TFS 455372
                    bCol_1 = g._getBindingColumn(ht_1.panel, ht_1.row, col);
                    if (this._divEdt && this._edtCol == bCol_1) {
                        this.closeEditor();
                        g.focus(); // TFS 275275
                    }
                    else if (bCol_1 != this._edtColPrev) {
                        setTimeout(function () {
                            _this.editColumnFilter(bCol_1, ht_1);
                        }, this._divEdt ? 100 : 0); // allow some time to close editors (TFS 117746)
                    }
                    return true;
                }
            }
            else {
                this.closeEditor(); // TFS 271847
            }
        }
        return false;
    };
    // show filter editor on alt+Down or alt+Up keys (like Excel)
    FlexGridFilter.prototype._keydown = function (e) {
        if (!e.defaultPrevented && !e.ctrlKey && e.altKey) {
            if (e.keyCode == Key.Down || e.keyCode == Key.Up) { // TFS 298285
                var g = this.grid, sel = g.selection, col = sel.col > -1 ? g.columns[sel.col] : null, bCol = col ? g._getBindingColumn(g.cells, sel.row, col) : null, // TFS 336015
                hasDropdown = bCol && bCol.dataMap && bCol.dataMapEditor == DataMapEditor.DropDownList, isColumnNotInFilterColumns = this.filterColumns && this.filterColumns.length > 0 && !this.filterColumns.includes(bCol._binding._path);
                // FilterEditor should respect FilterColumns property WJM-32139
                if (isColumnNotInFilterColumns) {
                    e.preventDefault();
                    return false;
                }
                // do not open filter, if selection in not on cells
                if (g.activePanelType != CellType.Cell && g.activePanelType != CellType.ColumnHeader) {
                    e.preventDefault();
                    return false;
                }
                // custom drop-down editor (TFS 442667)
                if (bCol && bCol.editor instanceof DropDown) {
                    hasDropdown = true;
                }
                if (bCol && !hasDropdown) { // if we have a column without a drop-down (TFS 419671)
                    var cf = this.getColumnFilter(bCol, false); // and we have a filter
                    if (cf && cf.filterType != FilterType.None) { // with type != None
                        this.editColumnFilter(bCol); // then show it
                        e.preventDefault(); // and kill the event
                        e.stopPropagation();
                    }
                }
            }
        }
    };
    FlexGridFilter._WJC_FILTER = FlexGridFilterClsNames.elemFilter;
    return FlexGridFilter;
}());
export { FlexGridFilter };
'use strict';
export var empty = {}; //this a placehorder to create exports object on module loader
'use strict';
/**
 * Defines a condition filter for a column on a {@link FlexGrid} control.
 *
 * Condition filters contain two conditions that may be combined
 * using an 'and' or an 'or' operator.
 *
 * This class is used by the {@link FlexGridFilter} class; you will
 * rarely use it directly.
 */
var ConditionFilter = /** @class */ (function () {
    /**
     * Initializes a new instance of the {@link ConditionFilter} class.
     *
     * @param column The column to filter.
     */
    function ConditionFilter(column) {
        this._c1 = new FilterCondition(this);
        this._c2 = new FilterCondition(this);
        this._and = true;
        this._col = column;
    }
    Object.defineProperty(ConditionFilter.prototype, "condition1", {
        /**
         * Gets the first condition in the filter.
         */
        get: function () {
            return this._c1;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConditionFilter.prototype, "condition2", {
        /**
         * Gets the second condition in the filter.
         */
        get: function () {
            return this._c2;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConditionFilter.prototype, "and", {
        /**
         * Gets a value that indicates whether to combine the two conditions
         * with an AND or an OR operator.
         *
         * The default value for this property is **true**.
         */
        get: function () {
            return this._and;
        },
        set: function (value) {
            this._and = asBoolean(value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConditionFilter.prototype, "dataMap", {
        /**
         * Gets or sets the {@link DataMap} used to convert raw values into display
         * values shown when editing this filter.
         */
        get: function () {
            return this._map;
        },
        set: function (value) {
            this._map = asType(value, DataMap, true);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConditionFilter.prototype, "column", {
        // ** IColumnFilter
        /**
         * Gets the {@link Column} to filter.
         */
        get: function () {
            return this._col;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConditionFilter.prototype, "isActive", {
        /**
         * Gets a value that indicates whether the filter is active.
         *
         * The filter is active if at least one of the two conditions
         * has its operator and value set to a valid combination.
         */
        get: function () {
            return this._c1.isActive || this._c2.isActive;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a value indicating whether a value passes this filter.
     *
     * @param value The value to test.
     */
    ConditionFilter.prototype.apply = function (value) {
        var col = this._col, c1 = this._c1, c2 = this._c2, dateOnly = false, timeOnly = false;
        // no binding or not active? accept everything
        if (!col || !col._binding || !this.isActive) {
            return true;
        }
        // retrieve the value
        value = col._binding.getValue(value);
        var map = this.dataMap || col.dataMap;
        if (map) { // TFS 357112
            value = map.getDisplayValue(value);
        }
        else if (isDate(value)) {
            dateOnly = !this._hasTimePart();
            timeOnly = !this._hasDatePart();
            // filter should have either date or time, UNLESS the user selected
            // an invalid format for a date field: TFS 395542
            //assert(!dateOnly || !timeOnly, 'Filter should have either date or time.');
            var refDt = FilterCondition._refDateTime;
            if (dateOnly) {
                value = DateTime.fromDateTime(value, refDt);
            }
            else if (timeOnly) {
                value = DateTime.fromDateTime(refDt, value);
            }
        }
        else if (isNumber(value)) { // use same precision for numbers (TFS 124098)
            var g = Globalize, fmt = col.format, strVal = g.formatNumber(value, fmt);
            value = g.parseFloat(strVal, fmt); // handle scaling: TFS 418348
        }
        // apply conditions
        var rv1 = c1.apply(value, dateOnly, timeOnly), rv2 = c2.apply(value, dateOnly, timeOnly);
        // combine results
        if (c1.isActive && c2.isActive) {
            return this._and ? (rv1 && rv2) : (rv1 || rv2);
        }
        else {
            return c1.isActive ? rv1 : c2.isActive ? rv2 : true;
        }
    };
    /**
     * Clears the filter.
     */
    ConditionFilter.prototype.clear = function () {
        this._c1.clear();
        this._c2.clear();
        this.and = true;
    };
    // ** implementation
    // checks whether a format has a date part
    ConditionFilter.prototype._hasDatePart = function () {
        var fmt = this._col.format;
        if (!fmt)
            return true; // default format is 'd'
        fmt = culture.Globalize.calendar.patterns[fmt] || fmt;
        return /[yMd]+/.test(fmt); // TFS 109409
    };
    // checks whether a format has a time part
    ConditionFilter.prototype._hasTimePart = function () {
        var fmt = this._col.format;
        if (!fmt)
            return false; // default format is 'd'
        fmt = culture.Globalize.calendar.patterns[fmt] || fmt;
        return /[Hmst]+/.test(fmt); // TFS 109409
    };
    // ** IQueryInterface
    /**
     * Returns true if this object supports a given interface.
     *
     * @param interfaceName Name of the interface to look for.
     */
    ConditionFilter.prototype.implementsInterface = function (interfaceName) {
        return interfaceName == 'IColumnFilter';
    };
    return ConditionFilter;
}());
export { ConditionFilter };
'use strict';
/**
 * The editor used to inspect and modify {@link ConditionFilter} objects.
 *
 * This class is used by the {@link FlexGridFilter} class; you
 * rarely use it directly.
 */
var ConditionFilterEditor = /** @class */ (function (_super) {
    __extends(ConditionFilterEditor, _super);
    /**
     * Initializes a new instance of the {@link ConditionFilterEditor} class.
     *
     * @param element The DOM element that hosts the control, or a selector
     * for the host element (e.g. '#theCtrl').
     * @param filter The {@link ConditionFilter} to edit.
     */
    function ConditionFilterEditor(element, filter) {
        var _this = _super.call(this, element) || this;
        _this._canApply = false; // TFS 454775
        // ** events
        /**
         * Occurs when the value of the {@linj canApply} property changes.
         */
        _this.canApplyChanged = new Event();
        // save reference to filter
        _this._filter = asType(filter, ConditionFilter, false);
        // instantiate and apply template
        var tpl = _this.getTemplate();
        _this.applyTemplate(ControlClsNames.hostElement + " " + ConditionFilterEditorClsNames.hostElement, tpl, {
            _divHdr: 'div-hdr',
            _divCmb1: 'div-cmb1',
            _divVal1: 'div-val1',
            _btnAnd: 'btn-and',
            _btnOr: 'btn-or',
            _spAnd: 'sp-and',
            _spOr: 'sp-or',
            _divCmb2: 'div-cmb2',
            _divVal2: 'div-val2',
        });
        // aria labels
        var ci = culture.FlexGridFilter, labels = ci.ariaLabels;
        setAriaLabel(_this._btnAnd, labels.and);
        setAriaLabel(_this._btnOr, labels.or);
        // localization
        setText(_this._divHdr, ci.header);
        setText(_this._spAnd, ci.and);
        setText(_this._spOr, ci.or);
        // add attribubute 'for' to label _divHdr (WJM-25857)
        var _id = getUniqueId('cmb1');
        _this._divHdr.htmlFor = _id;
        _this._divCmb1.id = _id;
        // create combos and value editors
        _this._cmb1 = _this._createOperatorCombo(_this._divCmb1, labels.op1);
        _this._cmb2 = _this._createOperatorCombo(_this._divCmb2, labels.op2);
        _this._val1 = _this._createValueInput(_this._divVal1, labels.val1);
        _this._val2 = _this._createValueInput(_this._divVal2, labels.val2);
        // disable value editors when no operators are selected
        _this._val1.isDisabled = true;
        _this._cmb1.selectedIndexChanged.addHandler(function (s, e) {
            _this._val1.isDisabled = s.selectedValue == null;
            _this.canApply = !_this._val1.isDisabled || !_this._val2.isDisabled;
        });
        _this._val2.isDisabled = true;
        _this._cmb2.selectedIndexChanged.addHandler(function (s, e) {
            _this._val2.isDisabled = s.selectedValue == null;
            _this.canApply = !_this._val1.isDisabled || !_this._val2.isDisabled;
        });
        // add event listeners for radio buttons
        var host = _this.hostElement;
        _this.addEventListener(host, 'change', _this._btnAndOrChanged.bind(_this));
        _this.addEventListener(host, 'keydown', _this._keydown.bind(_this));
        // initialize editor (after filter is ready: TFS 415316)
        setTimeout(function () {
            _this.updateEditor();
        });
        return _this;
    }
    Object.defineProperty(ConditionFilterEditor, "controlTemplate", {
        /**
         * Gets or sets the template used to instantiate {@link ConditionFilterEditor} controls.
         */
        get: function () {
            if (!ConditionFilterEditor._ctrlTemplate) {
                ConditionFilterEditor._ctrlTemplate = '<div>' +
                    '<label wj-part="div-hdr"></label>' +
                    '<div wj-part="div-cmb1"></div><br/>' +
                    '<div wj-part="div-val1"></div><br/>' +
                    '<div role="radiogroup" style="text-align:center">' +
                    '<label><input wj-part="btn-and" type="radio" role="radio"> <span wj-part="sp-and"></span> </label>&nbsp;&nbsp;&nbsp;' +
                    '<label><input wj-part="btn-or" type="radio" role="radio"> <span wj-part="sp-or"></span> </label>' +
                    '</div>' +
                    '<div wj-part="div-cmb2"></div><br/>' +
                    '<div wj-part="div-val2"></div><br/>' +
                    '</div>';
            }
            return ConditionFilterEditor._ctrlTemplate;
        },
        set: function (template) {
            ConditionFilterEditor._ctrlTemplate = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConditionFilterEditor.prototype, "filter", {
        /**
         * Gets a reference to the {@link ConditionFilter} being edited.
         */
        get: function () {
            return this._filter;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConditionFilterEditor.prototype, "canApply", {
        /**
         * Gets or sets a value that indicates whether the current edits
         * can be applied to make the filter active.
         */
        get: function () {
            return this._canApply;
        },
        set: function (value) {
            if (value != this._canApply) {
                this._canApply = value;
                this.onCanApplyChanged();
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Updates editor with current filter settings.
     */
    ConditionFilterEditor.prototype.updateEditor = function () {
        // initialize conditions
        var c1 = this._filter.condition1, c2 = this._filter.condition2;
        this._cmb1.selectedValue = c1.operator;
        this._cmb2.selectedValue = c2.operator;
        if (this._val1 instanceof input.ComboBox && !(this._val1 instanceof input.InputTime)) {
            this._val1.text = changeType(c1.value, DataType.String);
            this._val2.text = changeType(c2.value, DataType.String);
        }
        else {
            this._val1.value = c1.value;
            this._val2.value = c2.value;
        }
        // initialize and/or buttons
        var and = this._filter.and;
        this._checkRadio(this._btnAnd, and);
        this._checkRadio(this._btnOr, !and);
    };
    /**
     * Clears the editor without applying changes to the filter.
     */
    ConditionFilterEditor.prototype.clearEditor = function () {
        this._cmb1.selectedValue = this._cmb2.selectedValue = null;
        this._val1.text = this._val2.text = null;
        this._checkRadio(this._btnAnd, true);
        this._checkRadio(this._btnOr, false);
    };
    Object.defineProperty(ConditionFilterEditor.prototype, "isEditorClear", {
        /**
         * Gets a value that determines whether the editor has been cleared.
         */
        get: function () {
            return this._cmb1.selectedValue == null && !this._val1.text &&
                this._cmb2.selectedValue == null && !this._val2.text;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Updates filter to reflect the current editor values.
     */
    ConditionFilterEditor.prototype.updateFilter = function () {
        // update conditions
        var c1 = this._filter.condition1, c2 = this._filter.condition2;
        c1.operator = this._cmb1.selectedValue;
        c2.operator = this._cmb2.selectedValue;
        if ('value' in this._val1) { // TFS 372924
            c1.value = this._val1.value;
            c2.value = this._val2.value;
        }
        else {
            c1.value = this._getComboValue(this._val1); // TFS 372924, 392476
            c2.value = this._getComboValue(this._val2);
        }
        // update and/or operator
        this._filter.and = this._btnAnd.checked;
    };
    /**
     * Raises the {@link canApplyChanged} event.
     */
    ConditionFilterEditor.prototype.onCanApplyChanged = function (e) {
        this.canApplyChanged.raise(this, e);
    };
    // ** implementation
    // get the current combo value or text (TFS 372924, 392476)
    ConditionFilterEditor.prototype._getComboValue = function (cmb) {
        return cmb.selectedIndex > -1
            ? cmb.selectedValue
            : cmb.text;
    };
    // create operator combo
    ConditionFilterEditor.prototype._createOperatorCombo = function (element, label) {
        // get operator list based on column data type
        var col = this._filter.column, ci = culture.FlexGridFilter, list = ci.stringOperators, DT = DataType;
        if (!this._filter.dataMap && !col.dataMap) { // TFS 445187
            if (col.dataType == DT.Date) {
                list = ci.dateOperators;
            }
            else if (col.dataType == DT.Number) {
                list = ci.numberOperators;
            }
            else if (col.dataType == DT.Boolean) {
                list = ci.booleanOperators;
            }
        }
        // create and initialize the combo
        var cmb = new input.ComboBox(element, {
            itemsSource: list,
            displayMemberPath: 'name',
            selectedValuePath: 'op'
        });
        // apply aria label to input element
        setAriaLabel(cmb.inputElement, label);
        // return combo
        return cmb;
    };
    // create value input comntrol
    ConditionFilterEditor.prototype._createValueInput = function (e, label) {
        var f = this._filter, col = f.column, map = f.dataMap || col.dataMap, ctl = null, dt = DataType;
        if (col.dataType == dt.Date) {
            if (f._hasDatePart()) {
                ctl = f._hasTimePart()
                    ? new input.InputDateTime(e) // date and time
                    : new input.InputDate(e); // date only
            }
            else {
                ctl = new input.InputTime(e); // time only
            }
            ctl.format = col.format;
        }
        else if (col.dataType == dt.Number && !map) {
            ctl = new input.InputNumber(e);
            ctl.format = col.format;
        }
        else {
            ctl = new input.ComboBox(e);
            ctl.isEditable = true;
            ctl.trimText = false;
            if (map) {
                ctl.itemsSource = map.getDisplayValues();
                ctl.caseSensitiveSearch = col.grid // TFS 466659
                    ? col.grid.caseSensitiveSearch
                    : false;
            }
            else if (col.dataType == dt.Boolean) {
                ctl.itemsSource = [true, false];
            }
        }
        // never required
        ctl.isRequired = false;
        // apply aria label
        setAriaLabel(ctl.inputElement, label);
        // done
        return ctl;
    };
    // update and/or radio buttons
    // https://www.w3.org/TR/wai-aria-practices/examples/radio/radio-1/radio-1.html
    ConditionFilterEditor.prototype._btnAndOrChanged = function (e) {
        var and = e.target == this._btnAnd, or = e.target == this._btnOr;
        if (and || or) {
            this._checkRadio(this._btnAnd, and);
            this._checkRadio(this._btnOr, or);
        }
    };
    ConditionFilterEditor.prototype._checkRadio = function (radio, checked) {
        radio.checked = checked;
        setAttribute(radio, 'aria-checked', checked.toString());
        setAttribute(radio, 'tabindex', checked ? null : '-1'); // TFS 467337
    };
    // switch radio buttons with arrow keys (we have a 'roving tabindex')
    ConditionFilterEditor.prototype._keydown = function (e) {
        var and = e.target == this._btnAnd, or = e.target == this._btnOr;
        if (and || or) {
            switch (e.keyCode) {
                case Key.Left:
                case Key.Right:
                case Key.Up:
                case Key.Down:
                    var btn = (and) ? this._btnOr : this._btnAnd;
                    btn.click();
                    btn.focus();
                    e.preventDefault();
                    break;
            }
        }
    };
    ConditionFilterEditor._ctrlTemplate = "";
    return ConditionFilterEditor;
}(Control));
export { ConditionFilterEditor };
'use strict';
/**
 * Defines a filter condition.
 *
 * This class is used by the {@link FlexGridFilter} class;
 * you will rarely have to use it directly.
 */
var FilterCondition = /** @class */ (function () {
    /**
     * Initializes a new instance of the {@link FilterCondition} class.
     *
     * @param filter The {@link ConditionFilter} that owns this {@link FilterCondition}.
     */
    function FilterCondition(filter) {
        this._op = null;
        this._filter = filter;
    }
    Object.defineProperty(FilterCondition.prototype, "operator", {
        /**
         * Gets or sets the operator used by this {@link FilterCondition}.
         */
        get: function () {
            return this._op;
        },
        set: function (value) {
            this._op = asEnum(value, Operator, true);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FilterCondition.prototype, "value", {
        /**
         * Gets or sets the value used by this {@link FilterCondition}.
         */
        get: function () {
            return this._val;
        },
        set: function (value) {
            this._val = value;
            this._strVal = isString(value)
                ? this._getCaseString(value)
                : null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FilterCondition.prototype, "isActive", {
        /**
         * Gets a value that indicates whether the condition is active.
         */
        get: function () {
            switch (this._op) {
                // no operator
                case null:
                    return false;
                // equals/does not equal do not require a value (can compare to null)
                case Operator.EQ:
                case Operator.NE:
                    return true;
                // other operators require a value
                default:
                    return this._val != null || this._strVal != null;
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Clears the condition.
     */
    FilterCondition.prototype.clear = function () {
        this.operator = null;
        this.value = null;
    };
    /**
     * Returns a value that determines whether the given value passes this
     * {@link FilterCondition}.
     *
     * @param value The value to test.
     * @param dateOnly Whether to disregard the time part of **Date** values.
     * @param timeOnly Whether to disregard the date part of **Date** values.
     */
    FilterCondition.prototype.apply = function (value, dateOnly, timeOnly) {
        // condition value
        var thisVal = this._strVal || this._val;
        // use lower-case strings for all operations
        if (isString(value)) {
            value = this._getCaseString(value);
        }
        // treat null values as empty strings (TFS 247101)
        if (isString(thisVal) && value == null) {
            value = '';
        }
        // handle data/time only
        if (isDate(thisVal)) {
            if (dateOnly) {
                thisVal = DateTime.fromDateTime(thisVal, FilterCondition._refDateTime);
            }
            else if (timeOnly) {
                thisVal = DateTime.fromDateTime(FilterCondition._refDateTime, thisVal);
            }
        }
        // apply operator
        var op = Operator;
        switch (this._op) {
            case null:
                return true;
            case op.EQ:
                return value != null && thisVal != null
                    ? value.valueOf() == thisVal.valueOf()
                    : value == thisVal;
            case op.NE: // TFS 340151
                return value != null && thisVal != null
                    ? value.valueOf() != thisVal.valueOf()
                    : value != thisVal;
            case op.GT:
                return value > thisVal;
            case op.GE:
                return value >= thisVal;
            case op.LT:
                return value < thisVal;
            case op.LE:
                return value <= thisVal;
            case op.BW:
                return this._strVal != null && isString(value) // TFS 336790
                    ? value.indexOf(this._strVal) == 0
                    : false;
            case op.EW:
                return this._strVal != null && isString(value) && value.length >= this._strVal.length // TFS 336790
                    ? value.substr(value.length - this._strVal.length) == thisVal
                    : false;
            case op.CT:
                return this._strVal != null && isString(value) // TFS 336790
                    ? value.indexOf(this._strVal) > -1
                    : false;
            case op.NC:
                return this._strVal != null && isString(value) // TFS 336790
                    ? value.indexOf(this._strVal) < 0
                    : false;
            case op.NBW:
                return this._strVal != null && isString(value)
                    ? value.indexOf(this._strVal) !== 0
                    : false;
            case op.NEW:
                return this._strVal != null && isString(value)
                    ? value.substring(value.length - this._strVal.length) !== thisVal
                    : false;
        }
        throw 'Unknown operator';
    };
    // get a string taking into account the grid's caseSensitiveSearch property (TFS 466659)
    FilterCondition.prototype._getCaseString = function (value) {
        var col = this._filter.column, g = col ? col.grid : null;
        return !g || !g.caseSensitiveSearch
            ? value.toLowerCase()
            : value;
    };
    FilterCondition._refDateTime = new Date(2000, 0, 1, 0, 0, 0);
    return FilterCondition;
}());
export { FilterCondition };
/**
 * Specifies filter condition operators.
 */
export var Operator;
(function (Operator) {
    /** Equals. */
    Operator[Operator["EQ"] = 0] = "EQ";
    /** Does not equal. */
    Operator[Operator["NE"] = 1] = "NE";
    /** Greater than. */
    Operator[Operator["GT"] = 2] = "GT";
    /** Greater than or equal to. */
    Operator[Operator["GE"] = 3] = "GE";
    /** Less than. */
    Operator[Operator["LT"] = 4] = "LT";
    /** Less than or equal to. */
    Operator[Operator["LE"] = 5] = "LE";
    /** Begins with. */
    Operator[Operator["BW"] = 6] = "BW";
    /** Ends with. */
    Operator[Operator["EW"] = 7] = "EW";
    /** Contains. */
    Operator[Operator["CT"] = 8] = "CT";
    /** Does not contain. */
    Operator[Operator["NC"] = 9] = "NC";
    /** Does not begin with */
    Operator[Operator["NBW"] = 10] = "NBW";
    /** Does not end with */
    Operator[Operator["NEW"] = 11] = "NEW";
})(Operator || (Operator = {}));
'use strict';
/**
 * Specifies type of Search to use inside ValueFilterEditor.
 */
export var ValueFilterSearchKind;
(function (ValueFilterSearchKind) {
    /** Simple includes text filter. */
    ValueFilterSearchKind[ValueFilterSearchKind["Text"] = 0] = "Text";
    /** Regex filter. */
    ValueFilterSearchKind[ValueFilterSearchKind["Regex"] = 1] = "Regex";
    /** Excel style wilcard(?, *, ~) character support. */
    ValueFilterSearchKind[ValueFilterSearchKind["Excel"] = 2] = "Excel";
})(ValueFilterSearchKind || (ValueFilterSearchKind = {}));
/**
 * Defines a value filter for a column on a {@link FlexGrid} control.
 *
 * Value filters contain an explicit list of values that should be
 * displayed by the grid.
 */
var ValueFilter = /** @class */ (function () {
    /**
     * Initializes a new instance of the {@link ValueFilter} class.
     *
     * @param column The column to filter.
     */
    function ValueFilter(column) {
        this._values = null;
        this._filterText = null;
        this._xValueSearch = true;
        this._maxValues = 250;
        this._uniqueValues = null;
        this._sortValues = true;
        this._searchType = ValueFilterSearchKind.Excel;
        this._showIncludeSelection = false;
        this._col = column;
    }
    Object.defineProperty(ValueFilter.prototype, "searchType", {
        /**
         * Gets or sets the search type to use for ValueFilterEditor.
         *
         * For example, the code below sets up the ValueFilter for
         * 'country' column to use Excel style wildcards
         *
         * ```typescript
         * import { FlexGridFilter, ValueFilterSearchKind } from '@mescius/wijmo.grid.filter';
         * let filter = new FlexGridFilter(flex);
         * filter.getColumnFilter("country").valueFilter.searchType = ValueFilterSearchKind.Excel;
         * ```
         *
         * The default value for this property is **ValueFilterSearchKind.Excel**.
         */
        get: function () {
            return this._searchType;
        },
        set: function (value) {
            this._searchType = asEnum(value, ValueFilterSearchKind);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "showValues", {
        /**
         * Gets or sets an object with the selected (checked) values on the
         * value list.
         *
         * If the filter is not active, this property is set to null and all
         * values present in the data source are shown on the list.
         *
         * If the filter is active (the user selected some values from the list
         * but not all), the {@link showValues} property is set to an object
         * whose property names are the display values of the selected values.
         *
         * For example, if the value list contains country names and the user
         * selected "US" and "Japan", the {@link showValues} property returns:
         *
         * ```javascript
         * { Japan: true, US: true }
         * ```
         */
        get: function () {
            return this._values;
        },
        set: function (value) {
            this._values = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "filterText", {
        /**
         * Gets or sets a string used to filter the list of display values.
         */
        get: function () {
            return this._filterText;
        },
        set: function (value) {
            this._filterText = asString(value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "exclusiveValueSearch", {
        /**
         * Gets or sets a value that determines whether the filter should
         * include only values selected by the {@link filterText} property.
         *
         * The default value for this property is **true**, which matches
         * Excel's behavior.
         *
         * Set it to **false** to disable this behavior, so searching only
         * affects which items are displayed on the list and not which items
         * are included in the filter.
         */
        get: function () {
            return this._xValueSearch;
        },
        set: function (value) {
            value = asBoolean(value);
            if (value !== this._xValueSearch) {
                this._xValueSearch = asBoolean(value);
                if (value) {
                    this._filterText = ''; // clear search text to sync value-filter-editor with current filter
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "maxValues", {
        /**
         * Gets or sets the maximum number of elements on the list of display values.
         *
         * Adding too many items to the list makes searching difficult and hurts
         * performance. This property limits the number of items displayed at any time,
         * but users can still use the search box to filter the items they are
         * interested in.
         *
         * The default value for this property is **250**.
         *
         * This code changes the value to 1,000,000, effectively listing all unique
         * values for the field:
         *
         * ```typescript
         * import { FlexGridFilter} from '@mescius/wijmo.grid.filter';
         *
         * // change the maxItems property for the 'id' column:
         * let f = new FlexGridFilter(theGrid);
         * f.getColumnFilter('id').valueFilter.maxValues = 1000000;
         * ```
         */
        get: function () {
            return this._maxValues;
        },
        set: function (value) {
            this._maxValues = asNumber(value, false, true);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "uniqueValues", {
        /**
         * Gets or sets an array containing the unique values to be displayed on the list.
         *
         * If this property is set to null, the list will be filled based on the grid data.
         *
         * Explicitly assigning the list of unique values is more efficient than building
         * the list from the data, and is required for value filters to work properly when
         * the data is filtered on the server (because in this case some values might not
         * be present on the client so the list will be incomplete).
         *
         * By default, the filter editor will sort the unique values when displaying them
         * to the user. If you want to prevent that and show the values in the order you
         * provided, set the {@link sortValues} property to false.
         *
         * For example, the code below provides a list of countries to be used in the
         * {@link ValueFilter} for the column bound to the 'country' field:
         *
         * ```typescript
         * import { FlexGridFilter} from '@mescius/wijmo.grid.filter';
         *
         * // create filter for a FlexGrid
         * let filter = new FlexGridFilter(grid);
         *
         * // assign list of unique values to country filter
         * let cf = filter.getColumnFilter('country');
         * cf.valueFilter.uniqueValues = ['Austria', 'Belgium', 'Chile', 'Denmark'];
         * ```
         */
        get: function () {
            return this._uniqueValues;
        },
        set: function (value) {
            this._uniqueValues = asArray(value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "sortValues", {
        /**
         * Gets or sets a value that determines whether the values should be sorted
         * when displayed in the editor.
         *
         * This property is especially useful when you are using the {@link uniqueValues}
         * to provide a custom list of values property and you would like to preserve
         * the order of the values.
         */
        get: function () {
            return this._sortValues;
        },
        set: function (value) {
            this._sortValues = asBoolean(value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "dataMap", {
        /**
         * Gets or sets the {@link DataMap} used to convert raw values into display
         * values shown when editing this filter.
         */
        get: function () {
            return this._map;
        },
        set: function (value) {
            this._map = asType(value, DataMap, true);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "showIncludeSelection", {
        /**
         * Gets or sets a value that indicates whether the
         * value filter should display excel like 'Add current selection to filter' option
         * The default value for this property is **true**
         */
        get: function () {
            return this._showIncludeSelection;
        },
        set: function (value) {
            if (value != this._showIncludeSelection) {
                this._showIncludeSelection = asBoolean(value, false);
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "column", {
        // ** IColumnFilter
        /**
         * Gets the {@link Column} to filter.
         */
        get: function () {
            return this._col;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilter.prototype, "isActive", {
        /**
         * Gets a value that indicates whether the filter is active.
         *
         * The filter is active if some values are selected and some are not.
         * If all values are in the same state (either selected or un-selected),
         * then the filter is not active.
         */
        get: function () {
            return this._values != null && Object.keys(this._values).length > 0;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Gets a value that indicates whether a value passes the filter.
     *
     * @param value The value to test.
     */
    ValueFilter.prototype.apply = function (value) {
        var col = this.column;
        // no binding or no values? accept everything
        if (!col || !col._binding || !this._values || !Object.keys(this._values).length) {
            return true;
        }
        // retrieve the formatted value
        value = col._binding.getValue(value);
        value =
            this.dataMap ? this.dataMap.getDisplayValue(value) || '' : // TFS 440282
                col.dataMap ? col.dataMap.getDisplayValue(value) || '' :
                    Globalize.format(value, col.format);
        // apply conditions
        return this._values[value] != undefined;
    };
    /**
     * Clears the filter.
     */
    ValueFilter.prototype.clear = function () {
        this.showValues = null;
        this.filterText = null;
    };
    /**
     * Gets an array containing objects that represent all unique values
     * for this {@link column}.
     *
     * The objects in the array returned contain two properties:
     * *value* (the data value) and *text* (the formatted data value).
     *
     * If the {@link uniqueValues} property is set to an array of values,
     * that array is used as a data source.
     *
     * If {@link uniqueValues} is null, the method scans all items in the
     * data source and returns an creates an array containing all unique
     * values.
     *
     * This method is used by the {@link ValueFilterEditor} class to
     * populate the list of values shown to users.
     *
     * @param filtered Whether to apply all other filters when retrieving
     * the values from the data source.
     */
    ValueFilter.prototype.getUniqueValues = function (filtered) {
        if (filtered === void 0) { filtered = true; }
        var values = [], col = this.column, fmt = Globalize.format;
        // explicit list provided
        if (this.uniqueValues) {
            this.uniqueValues.forEach(function (value) {
                values.push({ value: value, text: fmt(value, col.format) });
            });
            return values;
        }
        // list not provided, get from data
        var keys = {}, view = col.collectionView, src = view ? view.sourceCollection : [];
        // apply all filters but this one (Excel-style filtering, TFS 133354)
        if (filtered && view && view.sourceCollection && view.filter) {
            try {
                // temporarily disable filtering for this column (TFS 467407, 467185)
                FlexGridFilter._skipColumn = this._col;
                // apply all other filters
                var nsrc = [];
                for (var i = 0; i < src.length; i++) {
                    if (view.filter(src[i])) {
                        nsrc.push(src[i]);
                    }
                }
                src = nsrc;
            }
            finally {
                // restore filtering for this column
                FlexGridFilter._skipColumn = null;
            }
        }
        // format and add unique values to the 'values' array
        for (var i = 0; i < src.length; i++) {
            var value = col._binding.getValue(src[i]), text = this.dataMap ? this.dataMap.getDisplayValue(value) || '' : // TFS 440282, 458349
                col.dataMap ? col.dataMap.getDisplayValue(value) || '' :
                    fmt(value, col.format);
            if (!keys[text]) {
                keys[text] = true;
                values.push({ value: value, text: text });
            }
        }
        // done 
        return values;
    };
    // ** IQueryInterface
    /**
     * Returns true if this object supports a given interface.
     *
     * @param interfaceName Name of the interface to look for.
     */
    ValueFilter.prototype.implementsInterface = function (interfaceName) {
        return interfaceName == 'IColumnFilter';
    };
    return ValueFilter;
}());
export { ValueFilter };
'use strict';
/**
 * The editor used to inspect and modify {@link ValueFilter} objects.
 *
 * This class is used by the {@link FlexGridFilter} class; you
 * rarely use it directly.
 */
var ValueFilterEditor = /** @class */ (function (_super) {
    __extends(ValueFilterEditor, _super);
    /**
     * Initializes a new instance of the {@link ValueFilterEditor} class.
     *
     * @param element The DOM element that hosts the control, or a selector
     * for the host element (e.g. '#theCtrl').
     * @param filter The {@link ValueFilter} to edit.
     */
    function ValueFilterEditor(element, filter) {
        var _this = _super.call(this, element) || this;
        _this._canApply = false; // TFS 454775
        _this._isFiltering = false; // WJM-25294
        // ** events
        /**
         * Occurs when the value of the {@linj canApply} property changes.
         */
        _this.canApplyChanged = new Event();
        // save reference to filter
        _this._filter = asType(filter, ValueFilter, false);
        // instantiate and apply template
        var tpl = _this.getTemplate();
        _this.applyTemplate(ControlClsNames.hostElement + " " + ValueFilterEditorClsNames.hostElement, tpl, {
            _divFilter: 'div-filter',
            _cbSelectAll: 'cb-select-all',
            _spSelectAll: 'sp-select-all',
            _cbIncludeSel: 'cb-include-sel',
            _spIncludeSel: 'sp-include-sel',
            _lblIncludeSel: 'lbl-include-sel',
            _divOptions: 'div-options',
            _divValues: 'div-values',
            _divExceeds: 'div-exceeds',
            _aExceeds: 'a-exceeds',
        });
        // include select all in tab order
        _this._cbSelectAll.tabIndex = 0;
        _this._cbIncludeSel.tabIndex = 0;
        // localization
        var ci = culture.FlexGridFilter;
        setText(_this._spSelectAll, ci.selectAll);
        setText(_this._spIncludeSel, ci.includeSelection);
        setText(_this._aExceeds, ci.exceedsMaxValues);
        // create sorted/filtered collection view with the values
        var view = _this._view = new CollectionView(null, {
            sortNulls: SortNulls.First,
            filter: _this._filterValues.bind(_this)
        });
        if (filter.sortValues) { // TFS 190560
            var sortBinding = filter.column.dataMap || filter.dataMap ? 'text' : 'value', asc = filter.column.dataType != DataType.Boolean; // TFS 229224
            view.sortDescriptions.push(new SortDescription(sortBinding, asc));
        }
        view.collectionChanged.addHandler(_this._updateSelectAllCheck, _this);
        // create search combo and value list
        _this._filterText = '';
        _this._rxFilter = null;
        _this._cmbFilter = new input.ComboBox(_this._divFilter, {
            trimText: false,
            isRequired: false,
            placeholder: ci.search
        });
        _this._cmbFilter.isEditable = true;
        _this._lbValues = new input.ListBox(_this._divValues, {
            displayMemberPath: 'text',
            checkedMemberPath: 'show',
            itemsSource: _this._view,
            itemFormatter: function (index, item) { return item ? item : ci.null; },
            checkedItemsChanged: function (s) { return _this._updateSelectAllCheck(); } // TFS 470645
        });
        setAttribute(_this._divValues, "aria-label", culture.FlexGridFilter.ariaLabels.listBox); // WJM-35183
        // add aria labels
        setAriaLabel(_this._cmbFilter.inputElement, ci.ariaLabels.search);
        setAttribute(_this._lbValues.hostElement, 'aria-multiselectable', true); // WJM-25151
        // add event listeners
        //this._cmbFilter.textChanged.addHandler(this._filterTextChanged, this); // this doesn't fire while composing
        _this._cmbFilter.inputElement.addEventListener('input', _this._filterTextChanged.bind(_this)); // this does: TFS 414983
        _this._cbSelectAll.addEventListener('click', _this._cbSelectAllClicked.bind(_this));
        if (!_this._popup) { //WJM-32950 
            _this._initPopup();
            // attach the event handler
            if (_this._aExceeds) {
                _this.addEventListener(_this._aExceeds, "click", function (e) {
                    e.preventDefault();
                    _this._popup.show(true);
                });
            }
        }
        // initialize editor (no timeout: TFS 433456)
        _this.updateEditor();
        // save list of initially checked items (TFS 454798)
        if (_this._filter.showValues) {
            _this._initialItems = view.sourceCollection.filter(function (item) { return (_this._filter.showValues[item.text || item.value] || false); });
        }
        else {
            _this._initialItems = _this._lbValues.checkedItems;
        }
        return _this;
    }
    Object.defineProperty(ValueFilterEditor, "controlTemplate", {
        /**
         * Gets or sets the template used to instantiate {@link ValueFilterEditor} controls.
         */
        get: function () {
            if (!ValueFilterEditor._ctrlTemplate) {
                ValueFilterEditor._ctrlTemplate = '<div>' +
                    '<div wj-part="div-filter"></div>' +
                    ("<div wj-part=\"div-options\" class=\"" + input.ListBoxClsNames.listBoxItem + "\">") +
                    '<label>' +
                    '<input wj-part="cb-select-all" type="checkbox"> ' +
                    '<span wj-part="sp-select-all"></span>' +
                    '</label><br />' +
                    '<label wj-part="lbl-include-sel">' +
                    '<input wj-part="cb-include-sel" type="checkbox"> ' +
                    '<span wj-part="sp-include-sel"></span>' +
                    '</label>' +
                    '</div>' +
                    '<div wj-part="div-values"></div>' +
                    '<div wj-part="div-exceeds">' +
                    '<a wj-part="a-exceeds"></a>' +
                    '</div>' +
                    '</div>';
            }
            return ValueFilterEditor._ctrlTemplate;
        },
        set: function (template) {
            ValueFilterEditor._ctrlTemplate = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilterEditor.prototype, "filter", {
        /**
         * Gets a reference to the {@link ValueFilter} being edited.
         */
        get: function () {
            return this._filter;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilterEditor.prototype, "canApply", {
        /**
         * Gets or sets a value that indicates whether the current edits
         * can be applied to make the filter active.
         */
        get: function () {
            return this._canApply;
        },
        set: function (value) {
            if (value != this._canApply) {
                this._canApply = value;
                this.onCanApplyChanged();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ValueFilterEditor.prototype, "_shouldIncludeSelection", {
        // returns whether to include existing selection into current filter
        get: function () {
            return this._filter.showIncludeSelection && this._cbIncludeSel.checked;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Updates editor with current filter settings.
     */
    ValueFilterEditor.prototype.updateEditor = function () {
        var _this = this;
        var col = this._filter.column, values = this._filter.getUniqueValues(true);
        // honor isContentHtml property
        this._lbValues.isContentHtml = col.isContentHtml;
        // check the items that are currently selected
        var showValues = this._filter.showValues;
        if (!showValues || Object.keys(showValues).length == 0) {
            values.forEach(function (value) { return value.show = true; });
        }
        else {
            for (var key in showValues) {
                for (var i = 0; i < values.length; i++) {
                    if (values[i].text == key) {
                        values[i].show = true;
                        break;
                    }
                }
            }
        }
        // update the filter
        var filterText = this._filter.filterText || '', caseSensitive = this._getCaseSensitive();
        this._cmbFilter.text = filterText;
        this._filterText = caseSensitive // TFS 466659
            ? filterText
            : filterText.toLowerCase();
        this._rxFilter = this._getSearchExp(filterText, caseSensitive);
        // add/remove includeSelection option
        if (this._filter.showIncludeSelection && !this.hostElement.contains(this._lblIncludeSel)) {
            this._divOptions.appendChild(this._lblIncludeSel);
        }
        else if (!this._filter.showIncludeSelection) {
            this._lblIncludeSel.remove();
        }
        // populate the list
        var view = this._view;
        view.pageSize = this._filter.maxValues; // WJM-19595
        view.sourceCollection = values;
        // patch: WJM-20495
        // fix item widths to fit maximum width to prevent cut-off of the selected item (WJM-20495)
        setTimeout(function () {
            _this._adjustWidths();
        }, 0);
        this._updateExceedsMaxValuesDisplay();
    };
    /**
     * Clears the editor without applying changes to the filter.
     *
     * @param checkAll Whether to check or uncheck all values
     * (either way, the filter is not applied in this case).
     */
    ValueFilterEditor.prototype.clearEditor = function (checkAll) {
        if (checkAll === void 0) { checkAll = true; }
        this._cmbFilter.text = '';
        this._filterText = '';
        this._rxFilter = null;
        var view = this._view;
        view.pageSize = 0; // TFS 288369
        view.items.forEach(function (value) {
            value.show = checkAll;
        });
        view.moveCurrentTo(-1); // avoid selecting one item if multiSel is false (TFS 420935)
        view.refresh();
        view.pageSize = this._filter.maxValues;
    };
    Object.defineProperty(ValueFilterEditor.prototype, "isEditorClear", {
        /**
         * Gets a value that determines whether the editor has been cleared.
         */
        get: function () {
            return !this._filterText && !this._cbSelectAll.indeterminate;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Updates filter to reflect the current editor values.
     */
    ValueFilterEditor.prototype._updateFilter = function () {
        // build list of values to show
        // (clear filter if all values are selected)
        var showValues = null, items = this._getItems();
        if (this._filterText || this._cbSelectAll.indeterminate) {
            showValues = (this._shouldIncludeSelection && this._filter.showValues) || {};
            for (var i = 0; i < items.length; i++) {
                var item = items[i];
                if (item.show) {
                    showValues[item.text] = true;
                }
            }
        }
        // update filter
        var filter = this._filter;
        filter.showValues = showValues;
        filter.filterText = this._filterText; // preserve search text
        // REVIEW: keep original behavior TFS 406309
        //filter.filterText = ''; // clear search text (Excel behavior, TFS 128910, 309205) 
    };
    /**
     * Updates filter to reflect the current editor values.
     * Delay to wait for filtering if needed (WJM-25294).
     */
    ValueFilterEditor.prototype.updateFilter = function () {
        var _this = this;
        if (this._isFiltering) {
            setTimeout(function () {
                _this._updateFilter();
            }, Control._SEARCH_DELAY + 100);
        }
        else {
            this._updateFilter();
        }
    };
    /**
     * Raises the {@link canApplyChanged} event.
     */
    ValueFilterEditor.prototype.onCanApplyChanged = function (e) {
        this.canApplyChanged.raise(this, e);
    };
    // ** implementation
    // gets the value of the owner grid's caseSensitiveSearch property
    ValueFilterEditor.prototype._getCaseSensitive = function () {
        var col = this._filter.column, g = col ? col.grid : null;
        return g && g.caseSensitiveSearch;
    };
    // gets an array with the filtered items or with all the items
    ValueFilterEditor.prototype._getItems = function () {
        return this._filter.exclusiveValueSearch
            ? this._view.items
            : this._view.sourceCollection;
    };
    // filter items on the list
    ValueFilterEditor.prototype._filterTextChanged = function () {
        var _this = this;
        if (((this._filterText !== '') || (!this._cbSelectAll.checked)) && (!this._filter.exclusiveValueSearch)) { //WJM-12383
            this._lbValues._addMode = true;
        }
        else {
            this._lbValues._addMode = false;
        }
        if (this._toFilter) {
            clearTimeout(this._toFilter);
        }
        this._isFiltering = true; // WJM-25294
        this._toFilter = setTimeout(function () {
            // get the filter text
            var filterText = _this._cmbFilter.text, caseSensitive = _this._getCaseSensitive();
            if (!caseSensitive) {
                filterText = filterText.toLowerCase();
            }
            // if it changed, apply the new filter
            if (filterText != _this._filterText) {
                _this._filterText = filterText;
                _this._rxFilter = _this._getSearchExp(filterText, caseSensitive);
                _this._view.refresh();
                // update the exceedsMaxValues warning.
                _this._updateExceedsMaxValuesDisplay();
                // if there's no filter, restore initial selection (TFS 454798)
                if (!filterText) {
                    var items = _this._initialItems;
                    if (items.length && _this._view.sourceCollection.indexOf(items[0]) > -1) { // TFS 470653
                        _this._lbValues.checkedItems = _this._initialItems;
                        _this._updateSelectAllCheck();
                        _this._isFiltering = false; // WJM-25294
                        return;
                    }
                }
                // there is a filter, select all items after filter changes (Excel behavior, TFS 128910, 309205)
                if (_this._filter.exclusiveValueSearch) {
                    _this._cbSelectAll.checked = true;
                    _this._cbSelectAllClicked();
                }
                else {
                    _this._updateSelectAllCheck();
                }
            }
            _this._isFiltering = false; // WJM-25294
        }, Control._SEARCH_DELAY);
    };
    // get search regex from search text
    ValueFilterEditor.prototype._getSearchExp = function (text, isCaseSensitive) {
        if (!text) {
            return null;
        }
        switch (this._filter.searchType) {
            case ValueFilterSearchKind.Text:
                return this._getTextSearchExp(text, isCaseSensitive);
            case ValueFilterSearchKind.Regex:
                return this._getRegexSearchExp(text, isCaseSensitive);
            case ValueFilterSearchKind.Excel:
                return this._getExcelSearchExp(text, isCaseSensitive);
        }
    };
    // use searchText as substring search and convert to equivalent regex
    ValueFilterEditor.prototype._getTextSearchExp = function (text, isCaseSensitive) {
        return new RegExp(escapeRegExp(text), isCaseSensitive ? '' : 'i');
    };
    // use search text as regex and convert to regex object
    ValueFilterEditor.prototype._getRegexSearchExp = function (text, isCaseSensitive) {
        try {
            return new RegExp((text), isCaseSensitive ? 's' : 'is');
        }
        catch (_a) {
            return new RegExp(('(?!)'), isCaseSensitive ? 's' : 'is');
        }
    };
    // use search text as excel style wildcard support and convert to equivalent regex
    ValueFilterEditor.prototype._getExcelSearchExp = function (text, isCaseSensitive) {
        var pattern = text;
        // filter regex char, except *,? 
        pattern = pattern.replace(/([.+^$[\]\\(){}|-])/g, '\\$1');
        // replace ~~, ~?, ~* with placeholder
        pattern = pattern.replace(/~~/g, '__`$0`__');
        pattern = pattern.replace(/~\?/g, '__`$1`__');
        pattern = pattern.replace(/~\*/g, '__`$2`__');
        // for single ~
        pattern = pattern.replace(/~(.)/g, '$1');
        // replace *, ? with proper regex chars
        pattern = pattern.replace(/(\?+)/g, function ($0, $1) {
            return Array.from($1).map(function (_) { return "."; }).join("");
        });
        pattern = pattern.replace(/(\*+)/g, function ($0, $1) {
            return Array.from($1).map(function (_) { return ".*"; }).join("");
        });
        // restore placeholders
        pattern = pattern.replace(/__`\$(\d)`__/g, function ($0, $1) {
            switch ($1) {
                case '0': return '~';
                case '1': return '\\?';
                case '2': return '\\*';
            }
        });
        // if text include excel wildcard, make sure only pattern is matched
        if ((/([^~]|^)[?*]/g).test(text)) {
            pattern = "^" + pattern + "$";
        }
        if ((text === "~") || text && !pattern.length) {
            pattern = '$a^'; // no result in case of ~
        }
        return new RegExp((pattern), isCaseSensitive ? 's' : 'is');
    };
    // filter values for display in the ListBox
    ValueFilterEditor.prototype._filterValues = function (value) {
        var rx = this._rxFilter;
        return rx != null && value != null
            ? rx.test(value.text)
            : true;
    };
    // handle clicks on 'Select All' checkbox
    ValueFilterEditor.prototype._cbSelectAllClicked = function () {
        var _this = this;
        var checked = this._cbSelectAll.checked, scrollTop = this._divValues.scrollTop, items = this._getItems();
        for (var i = 0; i < items.length; i++) {
            items[i].show = checked;
        }
        this._view.refresh();
        this._divValues.scrollTop = scrollTop; // TFS 335911
        setTimeout(function () {
            _this._adjustWidths();
        }, 0);
    };
    // update state of 'Select All' checkbox when values are checked/unchecked
    ValueFilterEditor.prototype._updateSelectAllCheck = function () {
        // count checked items
        var items = this._getItems(), checked = 0, unchecked = 0;
        for (var i = 0; i < items.length; i++) {
            if (items[i].show) {
                checked++;
            }
            else {
                unchecked++;
            }
            if (checked && unchecked) {
                break;
            }
        }
        // update checkbox
        setChecked(this._cbSelectAll, (checked && unchecked) ? null : checked > 0);
        // update canApply property
        this.canApply = checked > 0;
    };
    ValueFilterEditor.prototype._adjustWidths = function () {
        var lbHost = this._lbValues.hostElement;
        if (lbHost && lbHost.offsetHeight > 0) { // WJM-25294
            var maxWidth = 0, lbItemLabels = lbHost.querySelectorAll("." + input.ListBoxClsNames.listBoxItem + " label");
            if (lbItemLabels.length) {
                for (var i = 0; i < lbItemLabels.length; i++) {
                    maxWidth = Math.max(maxWidth, lbItemLabels[i].scrollWidth);
                }
                if (maxWidth) {
                    var lbItems = lbHost.querySelectorAll("." + input.ListBoxClsNames.listBoxItem);
                    if (lbItems.length) {
                        var firstItem = lbItems[0], cs = getComputedStyle(firstItem), padding = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
                        for (var i = 0; i < lbItems.length; i++) {
                            lbItems[i].style.width = maxWidth + padding + 'px';
                        }
                    }
                }
            }
        }
    };
    Object.defineProperty(ValueFilterEditor.prototype, "exceedsMaxValues", {
        // getter method to check if the unique values exceed the max allowed (WJM-32950, WJM-34007, WJM-34009)
        get: function () {
            if (this._view && this._view._view) {
                return (this._view._view.length > this.filter.maxValues);
            }
            return false;
        },
        enumerable: true,
        configurable: true
    });
    ValueFilterEditor.prototype._updateExceedsMaxValuesDisplay = function () {
        this._aExceeds.style.display = this.exceedsMaxValues ? "" : "none";
    };
    // initializes the popup (WJM-32950)
    ValueFilterEditor.prototype._initPopup = function () {
        var popupContent = document.createElement('div');
        popupContent.innerHTML = '<div  role="dialog">' +
            ("<div class=" + input.PopupClsNames.dialogBody + ">") +
            ("<div class=" + ValueFilterEditorClsNames.customModalText + ">") +
            '<p>' + culture.FlexGridFilter.popupBody + '</p>' +
            '</div>' +
            '</div>' +
            ("<div class=\"" + input.PopupClsNames.dialogFooter + " " + ValueFilterEditorClsNames.modalFooter + "\" >") +
            ("<button class=\"btn btn-primary " + UtilitesClsNames.hideOk + "\">") + culture.FlexGridFilter.popupOkButton + '</button>' +
            '</div>' +
            '</div>';
        this._popup = new input.Popup(popupContent, {
            modal: true,
            fadeIn: true,
            fadeOut: true
        });
    };
    ValueFilterEditor._ctrlTemplate = "";
    return ValueFilterEditor;
}(Control));
export { ValueFilterEditor };
'use strict';
/**
 * Defines a filter for a column on a {@link FlexGrid} control.
 *
 * The {@link ColumnFilter} contains a {@link ConditionFilter} and a
 * {@link ValueFilter}; only one of them may be active at a time.
 *
 * This class is used by the {@link FlexGridFilter} class; you
 * rarely use it directly.
 */
var ColumnFilter = /** @class */ (function () {
    /**
     * Initializes a new instance of the {@link ColumnFilter} class.
     *
     * @param owner The {@link FlexGridFilter} that owns this column filter.
     * @param column The {@link Column} to filter.
     */
    function ColumnFilter(owner, column) {
        var _this = this;
        this._owner = owner;
        this._col = column;
        this._valueFilter = new ValueFilter(column);
        this._valueFilter.exclusiveValueSearch = owner.exclusiveValueSearch;
        this._valueFilter.showIncludeSelection = owner.showIncludeSelection;
        this._conditionFilter = new ConditionFilter(column);
        owner.exclusiveValueSearchChanged.addHandler(function (s) {
            _this._valueFilter.exclusiveValueSearch = s.exclusiveValueSearch;
        });
        owner._showIncludeSelectionChanged.addHandler(function (s) {
            _this._valueFilter.showIncludeSelection = s.showIncludeSelection;
        });
    }
    Object.defineProperty(ColumnFilter.prototype, "filterType", {
        /**
         * Gets or sets the types of filtering provided by this filter.
         *
         * Setting this property to null causes the filter to use the value
         * defined by the owner filter's {@link FlexGridFilter.defaultFilterType}
         * property.
         */
        get: function () {
            return this._filterType != null ? this._filterType : this._owner.defaultFilterType;
        },
        set: function (value) {
            value = asEnum(value, FilterType, true);
            if (value != this._filterType) {
                var wasActive = this.isActive;
                this.clear();
                this._filterType = value;
                if (wasActive) {
                    this._owner.apply();
                }
                else if (this._col.grid) {
                    this._col.grid.invalidate();
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColumnFilter.prototype, "dataMap", {
        /**
         * Gets or sets the {@link DataMap} used to convert raw values into display
         * values shown when editing this filter.
         *
         * The example below assigns a {@link DataMap} to Boolean column filters
         * so the filter editor displays 'Yes' and 'No' instead of 'true' and 'false':
         *
         * ```typescript
         * import { FlexGridFilter } from '@mescius/wijmo.grid.filter';
         * var filter = new FlexGridFilter(grid),
         *     map = new wijmo.grid.DataMap([
         *             { value: true, caption: 'Yes' },
         *             { value: false, caption: 'No' },
         *         ], 'value', 'caption');
         * for (var c = 0; c &lt; grid.columns.length; c++) {
         *     if (grid.columns[c].dataType == wijmo.DataType.Boolean) {
         *         filter.getColumnFilter(c).dataMap = map;
         *     }
         * }
         * ```
         */
        get: function () {
            return this.conditionFilter.dataMap || this.valueFilter.dataMap;
        },
        set: function (value) {
            this.conditionFilter.dataMap = value;
            this.valueFilter.dataMap = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColumnFilter.prototype, "valueFilter", {
        /**
         * Gets the {@link ValueFilter} in this {@link ColumnFilter}.
         */
        get: function () {
            return this._valueFilter;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColumnFilter.prototype, "conditionFilter", {
        /**
         * Gets the {@link ConditionFilter} in this {@link ColumnFilter}.
         */
        get: function () {
            return this._conditionFilter;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColumnFilter.prototype, "column", {
        // ** IColumnFilter
        /**
         * Gets the {@link Column} being filtered.
         */
        get: function () {
            return this._col;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColumnFilter.prototype, "isActive", {
        /**
         * Gets a value that indicates whether the filter is active.
         */
        get: function () {
            return this._conditionFilter.isActive || this._valueFilter.isActive;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Gets a value that indicates whether a value passes the filter.
     *
     * @param value The value to test.
     */
    ColumnFilter.prototype.apply = function (value) {
        return this._conditionFilter.apply(value) && this._valueFilter.apply(value);
    };
    /**
     * Clears the filter.
     */
    ColumnFilter.prototype.clear = function () {
        this._valueFilter.clear();
        this._conditionFilter.clear();
    };
    // ** IQueryInterface
    /**
     * Returns true if this object supports a given interface.
     *
     * @param interfaceName Name of the interface to look for.
     */
    ColumnFilter.prototype.implementsInterface = function (interfaceName) {
        return interfaceName == 'IColumnFilter';
    };
    return ColumnFilter;
}());
export { ColumnFilter };
'use strict';
// globalization info
_addCultureInfo('FlexGridFilter', {
    // aria labels
    ariaLabels: {
        edit: 'Edit Filter for Column',
        dialog: 'Filter Editor for Column',
        asc: 'Sort Column in Ascending Order',
        dsc: 'Sort Column in Descending Order',
        search: 'Search Item List',
        op1: 'First Condition Operator',
        val1: 'First Condition Value',
        and: 'Require both Conditions',
        or: 'Require either Condition',
        op2: 'Second Condition Operator',
        val2: 'Second Condition Value',
        active: "active",
        listBox: 'List of options available for filtering'
    },
    // filter
    ascending: '\u2191 Ascending',
    descending: '\u2193 Descending',
    apply: 'Apply',
    cancel: 'Cancel',
    clear: 'Clear',
    conditions: 'Filter by Condition',
    values: 'Filter by Value',
    // value filter
    search: 'Search',
    selectAll: 'Select All',
    includeSelection: 'Include Current Selection',
    null: '(nothing)',
    // exceed max values
    exceedsMaxValues: 'Not all items showing',
    // popup message for exceed max values 
    popupBody: 'This column has more than 250 unique items. Only the first 250 unique items are displayed.',
    popupOkButton: 'OK',
    // condition filter
    header: 'Show items where the value',
    and: 'And',
    or: 'Or',
    stringOperators: [
        { name: '(not set)', op: null },
        { name: 'Equals', op: Operator.EQ },
        { name: 'Does not equal', op: Operator.NE },
        { name: 'Begins with', op: Operator.BW },
        { name: 'Does not begin with', op: Operator.NBW },
        { name: 'Ends with', op: Operator.EW },
        { name: 'Does not end with', op: Operator.NEW },
        { name: 'Contains', op: Operator.CT },
        { name: 'Does not contain', op: Operator.NC }
    ],
    numberOperators: [
        { name: '(not set)', op: null },
        { name: 'Equals', op: Operator.EQ },
        { name: 'Does not equal', op: Operator.NE },
        { name: 'Is Greater than', op: Operator.GT },
        { name: 'Is Greater than or equal to', op: Operator.GE },
        { name: 'Is Less than', op: Operator.LT },
        { name: 'Is Less than or equal to', op: Operator.LE }
    ],
    dateOperators: [
        { name: '(not set)', op: null },
        { name: 'Equals', op: Operator.EQ },
        { name: 'Is Before', op: Operator.LT },
        { name: 'Is After', op: Operator.GT }
    ],
    booleanOperators: [
        { name: '(not set)', op: null },
        { name: 'Equals', op: Operator.EQ },
        { name: 'Does not equal', op: Operator.NE }
    ]
});
/**
 * The editor used to inspect and modify column filters.
 *
 * This class is used by the {@link FlexGridFilter} class; you
 * rarely use it directly.
 */
var ColumnFilterEditor = /** @class */ (function (_super) {
    __extends(ColumnFilterEditor, _super);
    /**
     * Initializes a new instance of the {@link ColumnFilterEditor} class.
     *
     * @param element The DOM element that hosts the control, or a selector
     * for the host element (e.g. '#theCtrl').
     * @param filter The {@link ColumnFilter} to edit.
     * @param sortButtons Whether to show sort buttons in the editor.
     */
    function ColumnFilterEditor(element, filter, sortButtons) {
        if (sortButtons === void 0) { sortButtons = true; }
        var _this = _super.call(this, element, null, true) || this;
        _this._disallowedControls = new Set(["TransposedGrid", "WjTransposedGrid", "TransposedMultiRow", "WjTransposedMultiRow"]); // (WJM-22970)
        /**
         * Occurs after the filter is modified.
         */
        _this.filterChanged = new Event();
        /**
         * Occurs when one of the editor buttons is clicked.
         */
        _this.buttonClicked = new Event();
        // save reference to filter being edited
        _this._filter = asType(filter, ColumnFilter);
        // instantiate and apply template
        var tpl = _this.getTemplate();
        _this.applyTemplate(ControlClsNames.hostElement + " " + ControlClsNames.content + " " + ControlClsNames.content + " " + ColumnFilterEditorClsNames.hostElement, tpl, {
            _divSort: 'div-sort',
            _btnAsc: 'btn-asc',
            _btnDsc: 'btn-dsc',
            _divType: 'div-type',
            _aVal: 'a-val',
            _aCnd: 'a-cnd',
            _divEdtVal: 'div-edt-val',
            _divEdtCnd: 'div-edt-cnd',
            _btnApply: 'btn-apply',
            _btnCancel: 'btn-cancel',
            _btnClear: 'btn-clear'
        });
        // aria labels
        var ci = culture.FlexGridFilter, labels = ci.ariaLabels, host = _this.hostElement, col = _this.filter.column, view = col.grid.collectionView;
        setAttribute(host, 'role', 'dialog');
        setAriaLabel(host, labels.dialog + ' ' + col.header);
        setAriaLabel(_this._btnAsc, labels.asc);
        setAriaLabel(_this._btnDsc, labels.dsc);
        // localization
        setText(_this._btnAsc, ci.ascending);
        setText(_this._btnDsc, ci.descending);
        setText(_this._aVal, ci.values);
        setText(_this._aCnd, ci.conditions);
        setText(_this._btnApply, ci.apply);
        setText(_this._btnCancel, ci.cancel);
        setText(_this._btnClear, ci.clear);
        // show the filter that is active
        var ft = (_this.filter.conditionFilter.isActive || (filter.filterType & FilterType.Value) == 0)
            ? FilterType.Condition
            : FilterType.Value;
        _this._showFilter(ft);
        // disable sort buttons if allowSorting is set to false for Column (WJM-32138) and for TransposedGrid and TransposedMultirow as they do not support sorting (WJM-22970)
        if (!col.allowSorting || _this._disallowedControls.has(col.grid.constructor.name)) {
            enable(_this._btnAsc, false);
            enable(_this._btnDsc, false);
        }
        // update sort button state
        // hide sort buttons if the collection view is not sortable
        // or if the user doesn't want them
        if (!sortButtons || !view || !view.canSort) {
            _this._divSort.style.display = 'none';
        }
        _this._updateSortButtonState();
        // handle button clicks
        var bnd = _this._btnClicked.bind(_this), click = 'click';
        _this._btnApply.addEventListener(click, bnd);
        _this._btnCancel.addEventListener(click, bnd);
        _this._btnClear.addEventListener(click, bnd);
        _this._btnAsc.addEventListener(click, bnd);
        _this._btnDsc.addEventListener(click, bnd);
        _this._aVal.addEventListener(click, bnd);
        _this._aCnd.addEventListener(click, bnd);
        // commit/dismiss on Enter/Esc, press buttons on Enter/Space
        _this.addEventListener(host, 'keydown', function (e) {
            if (!e.defaultPrevented) {
                var isButton = e.target.tagName.match(/^(a|button)$/i);
                switch (e.keyCode) {
                    case Key.Space:
                        if (isButton) {
                            _this._btnClicked(e);
                            e.preventDefault();
                        }
                        break;
                    case Key.Enter:
                        if (isButton) {
                            _this._btnClicked(e); // TFS 123049
                        }
                        else {
                            _this.updateFilter();
                            _this.onFilterChanged();
                            _this.onButtonClicked();
                        }
                        e.preventDefault();
                        break;
                    case Key.Escape:
                        _this.onButtonClicked();
                        e.preventDefault();
                        break;
                    case Key.Tab:
                        moveFocus(_this.hostElement, e.shiftKey ? -1 : +1);
                        e.preventDefault();
                        break;
                }
            }
        });
        // close editor when the browser is resized
        // (or it will be in the wrong position... TFS 374361)
        _this.addEventListener(window, 'resize', function () {
            if (!_this.isTouching && !_this._wasTouching) { // TFS 281356
                _this.onButtonClicked();
            }
        });
        return _this;
    }
    Object.defineProperty(ColumnFilterEditor, "controlTemplate", {
        /**
         * Gets or sets the template used to instantiate {@link ColumnFilterEditor} controls.
         */
        get: function () {
            if (!ColumnFilterEditor._ctrlTemplate) {
                ColumnFilterEditor._ctrlTemplate = '<div>' +
                    ("<div wj-part=\"div-sort\" class=\"" + ColumnFilterEditorClsNames.sortButtons + "\">") +
                    ("<button wj-part=\"btn-asc\" class=\"" + InputFormElementsClsNames.btn + "\"></button>&nbsp;&nbsp;&nbsp;") +
                    ("<button wj-part=\"btn-dsc\" class=\"" + InputFormElementsClsNames.btn + "\"></button>") +
                    '</div>' +
                    ("<div wj-part=\"div-type\" class=\"" + ColumnFilterEditorClsNames.filterType + "\">") +
                    '<a wj-part="a-cnd" href="" draggable="false"></a>' +
                    '&nbsp;|&nbsp;' +
                    '<a wj-part="a-val" href="" draggable="false"></a>' +
                    '</div>' +
                    '<div wj-part="div-edt-val" tabindex="-1"></div>' +
                    '<div wj-part="div-edt-cnd" tabindex="-1"></div>' +
                    '<div style="text-align:right;margin-top:10px">' +
                    ("<button wj-part=\"btn-apply\" class=\"" + InputFormElementsClsNames.btn + "\"></button>&nbsp;&nbsp;") +
                    ("<button wj-part=\"btn-cancel\" class=\"" + InputFormElementsClsNames.btn + "\"></button>&nbsp;&nbsp;") +
                    ("<button wj-part=\"btn-clear\" class=\"" + InputFormElementsClsNames.btn + "\"></button>") +
                    '</div>' +
                    '</div>';
            }
            return ColumnFilterEditor._ctrlTemplate;
        },
        set: function (template) {
            ColumnFilterEditor._ctrlTemplate = template;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ColumnFilterEditor.prototype, "filter", {
        /**
         * Gets a reference to the {@link ColumnFilter} being edited.
         */
        get: function () {
            return this._filter;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Updates editor with current filter settings.
     */
    ColumnFilterEditor.prototype.updateEditor = function () {
        if (this._edtVal) {
            this._edtVal.updateEditor();
        }
        if (this._edtCnd) {
            this._edtCnd.updateEditor();
        }
    };
    /**
     * Updates filter with current editor settings.
     */
    ColumnFilterEditor.prototype.updateFilter = function () {
        switch (this._getFilterType()) {
            case FilterType.Value:
                this._edtVal.updateFilter();
                this.filter.conditionFilter.clear();
                break;
            case FilterType.Condition:
                this._edtCnd.updateFilter();
                this.filter.valueFilter.clear();
                break;
        }
    };
    /**
     * Raises the {@link filterChanged} event.
     */
    ColumnFilterEditor.prototype.onFilterChanged = function (e) {
        this.filterChanged.raise(this, e);
    };
    /**
     * Raises the {@link buttonClicked} event.
     */
    ColumnFilterEditor.prototype.onButtonClicked = function (e) {
        this.buttonClicked.raise(this, e);
    };
    // ** implementation
    // shows the value or condition filter editor
    ColumnFilterEditor.prototype._showFilter = function (filterType) {
        var _this = this;
        // save isTouching value to keep the editor up
        this._wasTouching = this.isTouching;
        // create editor if we have to
        if (filterType == FilterType.Value && this._edtVal == null) {
            this._edtVal = new ValueFilterEditor(this._divEdtVal, this.filter.valueFilter);
            this._edtVal.canApplyChanged.addHandler(function (s) {
                enable(_this._btnApply, _this._edtVal.canApply);
            });
        }
        if (filterType == FilterType.Condition && this._edtCnd == null) {
            this._edtCnd = new ConditionFilterEditor(this._divEdtCnd, this.filter.conditionFilter);
            this._edtCnd.canApplyChanged.addHandler(function (s) {
                enable(_this._btnApply, _this._edtCnd.canApply);
            });
        }
        // show selected editor
        if ((filterType & this.filter.filterType) != 0) {
            if (filterType == FilterType.Value) {
                this._divEdtVal.style.display = '';
                this._divEdtCnd.style.display = 'none';
                this._enableLink(this._aVal, false);
                this._enableLink(this._aCnd, true);
                this._edtVal.focus();
                this._edtVal._updateExceedsMaxValuesDisplay();
                enable(this._btnApply, this._edtVal.canApply);
            }
            else {
                this._divEdtVal.style.display = 'none';
                this._divEdtCnd.style.display = '';
                this._enableLink(this._aVal, true);
                this._enableLink(this._aCnd, false);
                this._edtCnd.focus();
                enable(this._btnApply, this._edtCnd.canApply);
            }
        }
        // hide switch button if only one filter type is supported
        var dtStyle = this._divType.style;
        switch (this.filter.filterType) {
            case FilterType.None:
            case FilterType.Condition:
            case FilterType.Value:
                dtStyle.display = 'none';
                break;
            default:
                dtStyle.display = '';
                break;
        }
    };
    // enable/disable filter switch links
    ColumnFilterEditor.prototype._enableLink = function (a, enable) {
        toggleClass(a, ControlStateClsNames.disabled, !enable);
        setAttribute(a, 'href', enable ? '' : null);
        setAttribute(a, 'disabled', enable ? null : 'disabled');
    };
    // update the sort button state (TFS 454776)
    ColumnFilterEditor.prototype._updateSortButtonState = function () {
        var col = this.filter.column, sort = col ? col.currentSort : '', cls = ControlStateClsNames.active, ariaLabels = culture.FlexGridFilter.ariaLabels;
        if (sort === '+') {
            addClass(this._btnAsc, cls);
            this._btnAsc.setAttribute("aria-label", ariaLabels.asc + " " + ariaLabels.active);
        }
        else if (sort === '-') {
            addClass(this._btnDsc, cls);
            this._btnDsc.setAttribute("aria-label", ariaLabels.dsc + " " + ariaLabels.active);
        }
    };
    // gets the type of filter currently being edited
    ColumnFilterEditor.prototype._getFilterType = function () {
        var ft = FilterType;
        return this._divEdtVal.style.display != 'none' ? ft.Value : ft.Condition;
    };
    // handle buttons
    ColumnFilterEditor.prototype._btnClicked = function (e) {
        var _this = this;
        var target = e.target;
        e.preventDefault();
        e.stopPropagation();
        // ignore disabled elements
        if (hasClass(target, ControlStateClsNames.disabled)) {
            return;
        }
        // switch filters
        if (target == this._aVal) {
            this._showFilter(FilterType.Value);
            moveFocus(this._edtVal.hostElement, 0);
            return;
        }
        if (target == this._aCnd) {
            this._showFilter(FilterType.Condition);
            moveFocus(this._edtCnd.hostElement, 0);
            return;
        }
        // apply sort
        if (target == this._btnAsc || target == this._btnDsc) {
            var g = this.filter.column.grid, col = this.filter.column, binding_1 = col.sortMemberPath || col.binding, view = col.grid.collectionView, sds_1 = view.sortDescriptions, ownerCol = this.hostElement["$WJ-OWNR"] && this.hostElement["$WJ-OWNR"]["wj-cell-index"];
            if (ownerCol) {
                var gridPanel = ownerCol.panel, cellRng = ownerCol.rng;
                if (cellRng == null) { // range is null for non-group columns (WJM-22970)
                    cellRng = new CellRange(ownerCol.row, ownerCol.col, ownerCol.row, ownerCol.col);
                }
                var args = new CellRangeEventArgs(gridPanel, cellRng, e);
                if (g.onSortingColumn(args)) { // fire sortingColumn event (WJM-22970)
                    sds_1.deferUpdate(function () {
                        sds_1.clear();
                        sds_1.push(new SortDescription(binding_1, e.target == _this._btnAsc));
                    });
                    this._updateSortButtonState();
                    g.onSortedColumn(args); // fire sortedColumn event (WJM-22970)
                }
            }
        }
        // apply/clear filter
        if (target == this._btnApply) {
            this.updateFilter();
            this.onFilterChanged();
        }
        else if (target == this._btnClear) {
            if (this.filter.isActive) {
                this.filter.clear();
                this.onFilterChanged();
            }
        }
        else {
            this.updateEditor(); // show current filter state
        }
        // raise event so caller can close the editor and apply the new filter
        this.onButtonClicked();
    };
    ColumnFilterEditor._ctrlTemplate = "";
    return ColumnFilterEditor;
}(Control));
export { ColumnFilterEditor };
// Entry file. All real code files should be re-exported from here.
_registerModule('wijmo.grid.filter', selfModule);
